/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { graphql, Link } from 'gatsby';
import { DashboardLayout } from '../modules/layout/components/dashboard-layout';
import { IHSRCharacter } from '../modules/common/model/graphql-types';
import { getImage, GatsbyImage } from 'gatsby-plugin-image';

import './employee.scss';
import './hsr-character-dynamic.scss';
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Row,
  Tab,
  Tabs
} from 'react-bootstrap';
import { Seo } from '../modules/common/components/seo';
import { RatingBox } from '../modules/common/components/rating-box';
import { Options } from '@contentful/rich-text-react-renderer';
import { INLINES, BLOCKS } from '@contentful/rich-text-types';
import { HSRCharacter } from '../modules/hsr/common/components/hsr-character';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faBriefcaseMedical,
  faChartLine,
  faCircleInfo,
  faDiagramProject,
  faDungeon,
  faFlask,
  faHandFist,
  faSquare,
  faVirus
} from '@fortawesome/free-solid-svg-icons';
import { HSRStat } from '../modules/hsr/common/components/hsr-stat';
import { HSRCalculateStats } from '../modules/hsr/utils/hsr-stat-calc';
import Select from 'react-select';
import Switch from 'react-switch';
import { HSRItem } from '../modules/hsr/common/components/hsr-item';
import { YoutubeEmbed } from '../modules/common/components/youtube-embed';
import { HSRRelicSet } from '../modules/hsr/common/components/hsr-relic-set';
import { HSRLightCone } from '../modules/hsr/common/components/hsr-light-cone';
import { HSRSkill } from '../modules/hsr/skills/hsr-skill';
import { HSRDPSRankingArrayNew } from '../modules/hsr/dps-rankings/characters-array-new';
import { MoCDuos } from '../modules/hsr/moc/duos';
import { ProfileFuEHP } from '../modules/hsr/profile/fu-ehp';
import { MoCandPFCharData } from '../modules/hsr/char-stats/char-moc-pf-data';
import { MoCCharacterData } from '../modules/hsr/moc/char-stats';
import { PFCharacterData } from '../modules/hsr/pure/chars-stats';
import { MoCCurrentAll } from '../modules/hsr/moc/current-all';
import { PFDuos } from '../modules/hsr/pure/duos';
import { ProfileSparkleBronya } from '../modules/hsr/profile/sparkle-bronya';
import { AveProfileStats } from '../modules/hsr/profile/ave-stats';
import { AveProfileCalcs } from '../modules/hsr/profile/ave-calcs';
import { RobinCalcs } from '../modules/hsr/profile/robin-calcs';
import { ProfileHarmonyTricks } from '../modules/hsr/profile/harmony-tips';
import { ProfileHarmonyEnergy } from '../modules/hsr/profile/harmony-energy';
import { ProfileHarmonyDPS } from '../modules/hsr/profile/harmony-calcs';
import { ProfileFireflyInvest } from '../modules/hsr/profile/firefly-invest';
import { ProfileSynergies } from '../modules/hsr/profile/all-synergies';
import { ApoCharacterData } from '../modules/hsr/apo/char-stats';
import { ApoDuos } from '../modules/hsr/apo/duos';
import { ProfileYunliTaunt } from '../modules/hsr/profile/yunli-taunt';
import { ProfileJiaoqiuDPS } from '../modules/hsr/profile/jiaoqiu-calcs';
import { HSRLastUpdate } from '../modules/hsr/profile/last-update';
import { ProfileLingshaTable } from '../modules/hsr/profile/profile-lingsha-table';
import { ProfileRobinTricks } from '../modules/hsr/profile/robin-tips';
import { HSRProfileRelics } from '../modules/hsr/profile/characters-relics';
import { HSRProfileCones } from '../modules/hsr/profile/characters-cones';
import { ProfileMCvsFugue } from '../modules/hsr/profile/mc-fugue';
import { ProfileHertaDPS } from '../modules/hsr/profile/the-herta-calcs';
import { AglaeaSpeedTuning } from '../modules/hsr/profile/aglaea-speed';
import { HSRRatings } from '../modules/hsr/data/hsr-ratings';
import { ProfileTribbieDPS } from '../modules/hsr/profile/tribbie-calcs';
import { TribbieSpeedTuning } from '../modules/hsr/profile/tribbie-speed';
import { ProfileMydeiDPS } from '../modules/hsr/profile/mydei-calcs';
import { ProfileCastoriceDPS } from '../modules/hsr/profile/castorice-calcs';
import { ProfileCastoInvest } from '../modules/hsr/profile/castorice-invest';

const Text = ({ children }) => <p>{children}</p>;

const options: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      if (node.data.uri.startsWith('/')) {
        return <Link to={node.data.uri}>{children}</Link>;
      } else {
        return (
          <a href={node.data.uri} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      }
    },
    [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
    ['BLOCKS.EMBEDDED_ASSET']: (node) => {
      return (
        <>
          <pre>
            <code>{JSON.stringify(node, null, 2)}</code>
          </pre>
        </>
      );
    },
    'embedded-asset-block': (node) => {
      const { localFile } = node.data.target;
      if (!localFile || !localFile.childImageSharp) {
        return null;
      }
      const image = getImage(localFile.childImageSharp);
      return <GatsbyImage image={image} alt="" />;
    }
  }
};

interface IHSRCharacterNodes {
  nodes: IHSRCharacter[];
}

interface IHSRCharacterEntry {
  currentUnit: IHSRCharacterNodes;
}

interface IProps {
  data: IHSRCharacterEntry;
}

const HSRCharacterDetails: React.FC<IProps> = ({ data }) => {
  const character = data.currentUnit.nodes[0];
  const [level, setLevel] = useState(80);
  const [enableAscension, setEnableAscension] = useState(true);
  const [currentTab, setCurrentTab] = useState('Info');
  const [currentMode, setCurrentMode] = useState('MOC');
  const [mocData, setMocData] = useState(MoCCharacterData);
  const [pfData, setPFData] = useState(PFCharacterData);
  const [asData, setASData] = useState(ApoCharacterData);
  const [mocPfData, setMocPfData] = useState(MoCandPFCharData);
  const [mocTeams, setMocTeams] = useState(
    MoCCurrentAll.filter((team) => {
      if (team.char_one.toLowerCase() === character.slug) return true;
      if (team.char_two.toLowerCase() === character.slug) return true;
      if (team.char_three.toLowerCase() === character.slug) return true;
      if (team.char_four.toLowerCase() === character.slug) return true;
    })
  );
  const [dpsRanking, setDpsRanking] = useState(
    HSRDPSRankingArrayNew.filter((rankingCharacter) => {
      if (rankingCharacter.slug === character.slug) return true;
    })
  );

  const fullImage = character.fullImage
    ? getImage(character.fullImage.localFile.childImageSharp)
    : null;

  const profileImage = character.cardImage
    ? getImage(character.cardImage.localFile.childImageSharp)
    : null;

  const levelOption = [
    { value: 1, label: 'Level 1' },
    { value: 20, label: 'Level 20' },
    { value: 30, label: 'Level 30' },
    { value: 40, label: 'Level 40' },
    { value: 50, label: 'Level 50' },
    { value: 60, label: 'Level 60' },
    { value: 70, label: 'Level 70' },
    { value: 80, label: 'Level 80' }
  ];

  let defaultLevel = level
    ? levelOption.find((opt) => opt.value === level)
    : null;

  if (defaultLevel === null) {
    defaultLevel = levelOption[7];
    setLevel(defaultLevel.value);
  }

  const levelChangeHandler = (e) => {
    if (e.value === 1) {
      setEnableAscension(false);
    }
    if (e.value === 80) {
      setEnableAscension(true);
    }
    setLevel(e.value);
  };

  const items = mocTeams.slice(0, 10).map((item, index) => (
    <div className="team-row" key={index}>
      <div className="column info">
        <p className="rank">Rank {item.rank}</p>
        <p className="usage">App. rate: {item.app_rate}%</p>
        <p className="rounds">Avg. cycles: {item.avg_round}</p>
      </div>
      <div className="column characters">
        <HSRCharacter
          slug={item.char_one.toLowerCase()}
          mode="icon"
          showIcon
          enablePopover
        />
        <HSRCharacter
          slug={item.char_two.toLowerCase()}
          mode="icon"
          showIcon
          enablePopover
        />
        <HSRCharacter
          slug={item.char_three.toLowerCase()}
          mode="icon"
          showIcon
          enablePopover
        />
        <HSRCharacter
          slug={item.char_four.toLowerCase()}
          mode="icon"
          showIcon
          enablePopover
        />
      </div>
    </div>
  ));

  return (
    <DashboardLayout
      className={'generic-page character-page character-hsr-v2'}
      game="hsr"
    >
      <ul className="breadcrumb">
        <li>
          <Link to="/star-rail">Honkai: Star Rail</Link>
        </li>
        <li className="divider">/</li>
        <li>
          <Link to="/star-rail/characters">Characters</Link>
        </li>
        <li className="divider">/</li>
        <li>{character.name}</li>
      </ul>
      <>
        <div className="character-top">
          <div className="left-info">
            <h1>
              <span className="small">Honkai: Star Rail</span>
              <strong className={`${character.element}`}>
                {character.name}
              </strong>
              <span className="sub">Best build, teams and other guides</span>
            </h1>
          </div>
          <div className="right-image">
            <div
              className={`character-background ${character.element} ${character.slug}`}
            ></div>
            <GatsbyImage image={profileImage} alt="Character" />
          </div>
        </div>
        <Row className="intro-section">
          <Col xs={12} xl={6} xxl={character.videos ? 6 : 9}>
            <div className={`content-header ${character.element}`}>
              <FontAwesomeIcon icon={faSquare} width="18" /> Introduction
            </div>
            <div className="character-intro">
              <div className="combined">
                <h2>
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  is a{' '}
                  <strong className={`rarity-${character.rarity}`}>
                    {character.rarity}★
                  </strong>{' '}
                  character from the{' '}
                  <strong className={`${character.element}`}>
                    {character.element}
                  </strong>{' '}
                  element who follows the{' '}
                  <strong className={`${character.path}`}>
                    Path of {character.path}
                  </strong>
                  .
                </h2>
                {character.description ? (
                  <>{renderRichText(character.description, options)}</>
                ) : (
                  ''
                )}
              </div>
              <p className="hide-on-mobile">
                To learn more about{' '}
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                check the sections below. <strong>Use the tabs</strong> to
                quickly switch to the kind of information you're looking for -
                kit, traces, review or build guides.
              </p>
            </div>
          </Col>
          <Col xs={12} xl={6} xxl={character.videos ? 6 : 3}>
            <div className="video-promo ">
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Video guide
              </div>
              <div className="video-guide">
                {character.videos ? (
                  <>
                    <div>
                      <p>
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        has at least one video guide available, so if you prefer
                        this sort of medium to learn more about a character,
                        check it out:
                      </p>
                    </div>
                    {character.videos.map((videoLink, idx) => {
                      return (
                        <Row key={idx} className="video-row">
                          {idx === 0 && (
                            <Col xs={12} lg={6}>
                              <YoutubeEmbed embedId={videoLink.video} />
                            </Col>
                          )}
                        </Row>
                      );
                    })}
                  </>
                ) : (
                  <p>This character has no video guide yet.</p>
                )}
              </div>
            </div>
          </Col>
        </Row>
        {character.isReleased === true && (
          <>
            <HSRLastUpdate
              slug={character.slug}
              element={character.element}
              update={character.updatedAt}
            />
          </>
        )}
        <div
          className="fuse-ad-placeholder bigger"
          data-fuse="22844297232"
        ></div>
        <p className="show-on-mobile">
          To learn more about{' '}
          <strong className={`${character.element}`}>{character.name}</strong>{' '}
          check the sections below. <strong>Use the tabs</strong> to quickly
          switch to the kind of information you're looking for - kit, traces,
          review or build guides.
        </p>
        {/* {character.slug === 'rappa' && (
          <>
            <div className={`content-header ${character.element}`}>
              <FontAwesomeIcon icon={faSquare} width="18" /> Special code
            </div>
            <p>
              Also, here's a <strong>special code</strong> to celebrate Rappa's
              release!
            </p>
            <div className="codes">
              <Box isCentered>
                <p className="code">KFWJD7Y2G9S5H</p>
                <p className="date">Released on 23.10.2024</p>
              </Box>
            </div>
          </>
        )} */}
        {/* {character.slug === 'castorice' && (
          <>
            <div className="early-info rappa">
              <p className="special">SPECIAL!</p>
              <p>
                Check out our <strong>first impressions</strong> of Castorice!
              </p>
              <Link to="/star-rail/guides/early-impressions-castorice">
                <Button variant="white">Read more</Button>
              </Link>
            </div>
          </>
        )} */}
        <div className="tabs">
          <div
            className={`single-tab ${character.element} ${
              currentTab === 'Info' ? 'active' : ''
            }`}
            onClick={() => setCurrentTab('Info')}
          >
            <div className="top-icon">
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
            </div>
            <p>Profile</p>
          </div>
          <div
            className={`single-tab ${character.element} ${
              currentTab === 'Review' ? 'active' : ''
            }`}
            onClick={() => setCurrentTab('Review')}
          >
            <div className="top-icon">
              <FontAwesomeIcon icon={faBook} width="18" />
            </div>
            <p>Review</p>
          </div>
          <div
            className={`single-tab ${character.element} ${
              currentTab === 'Build' ? 'active' : ''
            }`}
            onClick={() => setCurrentTab('Build')}
          >
            <div className="top-icon">
              <FontAwesomeIcon icon={faDiagramProject} width="18" />
            </div>
            <p>Build and teams</p>
          </div>
          <div
            className={`single-tab ${character.element} ${
              currentTab === 'MoC' ? 'active' : ''
            }`}
            onClick={() => setCurrentTab('MoC')}
          >
            <div className="top-icon">
              <FontAwesomeIcon icon={faDungeon} width="18" />
            </div>
            <p>MoC/PF/AS Statistics</p>
          </div>
          <div
            className={`single-tab ${character.element} ${
              currentTab === 'Performance' ? 'active' : ''
            }`}
            onClick={() => setCurrentTab('Performance')}
          >
            <div className="top-icon">
              <FontAwesomeIcon icon={faChartLine} width="18" />
            </div>
            <p>Calculations</p>
          </div>
        </div>
        <div className={`tab-inside ${currentTab === 'Info' ? 'active' : ''}`}>
          <div className={`mobile-header ${character.element}`}>
            <p>
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
              Profile
            </p>
          </div>
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Skills
          </div>
          {character.skillsNew ? (
            <>
              <div className={`skills ${character.element}`}>
                <Row xs={1} xl={2} xxl={2}>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={character.skillsNew.basic.id}
                        maxLevel={7}
                        element={character.element}
                        gen={character.skillsNew.basic.energy_gen}
                        break_primary={character.skillsNew.basic.break_primary}
                        break_secondary={
                          character.skillsNew.basic.break_secondary
                        }
                        disabled={character.slug === 'test' ? true : false}
                      />
                      {character.skillsNew.basic_enh.id && (
                        <HSRSkill
                          id={character.skillsNew.basic_enh.id}
                          maxLevel={7}
                          element={character.element}
                          gen={character.skillsNew.basic_enh.energy_gen}
                          break_primary={
                            character.skillsNew.basic_enh.break_primary
                          }
                          break_secondary={
                            character.skillsNew.basic_enh.break_secondary
                          }
                          isEnhanced={true}
                        />
                      )}
                      {character.skillsNew.basic_enh2.id && (
                        <HSRSkill
                          id={character.skillsNew.basic_enh2.id}
                          maxLevel={7}
                          element={character.element}
                          gen={character.skillsNew.basic_enh2.energy_gen}
                          break_primary={
                            character.skillsNew.basic_enh2.break_primary
                          }
                          break_secondary={
                            character.skillsNew.basic_enh2.break_secondary
                          }
                          isEnhanced={true}
                        />
                      )}
                      {character.skillsNew.basic_enh3.id && (
                        <HSRSkill
                          id={character.skillsNew.basic_enh3.id}
                          maxLevel={7}
                          element={character.element}
                          gen={character.skillsNew.basic_enh3.energy_gen}
                          break_primary={
                            character.skillsNew.basic_enh3.break_primary
                          }
                          break_secondary={
                            character.skillsNew.basic_enh3.break_secondary
                          }
                          isEnhanced={true}
                        />
                      )}
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={character.skillsNew.skill.id}
                        maxLevel={character.slug === 'test' ? 8 : 12}
                        element={character.element}
                        gen={character.skillsNew.skill.energy_gen}
                        break_primary={character.skillsNew.skill.break_primary}
                        break_secondary={
                          character.skillsNew.skill.break_secondary
                        }
                        disabled={character.slug === 'test' ? true : false}
                      />
                      {character.skillsNew.skill_enh.id && (
                        <HSRSkill
                          id={character.skillsNew.skill_enh.id}
                          maxLevel={12}
                          element={character.element}
                          gen={character.skillsNew.skill_enh.energy_gen}
                          break_primary={
                            character.skillsNew.skill_enh.break_primary
                          }
                          break_secondary={
                            character.skillsNew.skill_enh.break_secondary
                          }
                          isEnhanced={true}
                        />
                      )}
                      {character.slug === 'mydei' && (
                        <HSRSkill
                          id="140411"
                          maxLevel={12}
                          element={character.element}
                          gen="10"
                          break_primary="90"
                          break_secondary="60"
                          isEnhanced={true}
                        />
                      )}
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={character.skillsNew.ult.id}
                        maxLevel={character.slug === 'test' ? 8 : 12}
                        element={character.element}
                        gen={character.skillsNew.ult.energy_gen}
                        break_primary={character.skillsNew.ult.break_primary}
                        break_secondary={
                          character.skillsNew.ult.break_secondary
                        }
                        ult_cost={character.energyUltimate}
                        disabled={character.slug === 'test' ? true : false}
                      />
                      {character.skillsNew.ult_enh.id && (
                        <HSRSkill
                          id={character.skillsNew.ult_enh.id}
                          maxLevel={12}
                          element={character.element}
                          gen={character.skillsNew.ult_enh.energy_gen}
                          break_primary={
                            character.skillsNew.ult_enh.break_primary
                          }
                          break_secondary={
                            character.skillsNew.ult_enh.break_secondary
                          }
                          ult_cost={
                            character.slug === 'argenti'
                              ? 180
                              : character.energyUltimate
                          }
                        />
                      )}
                      {character.skillsNew.ult_enh2 &&
                        character.skillsNew.ult_enh2.id && (
                          <HSRSkill
                            id={character.skillsNew.ult_enh2.id}
                            maxLevel={12}
                            element={character.element}
                            gen={character.skillsNew.ult_enh2.energy_gen}
                            break_primary={
                              character.skillsNew.ult_enh2.break_primary
                            }
                            break_secondary={
                              character.skillsNew.ult_enh2.break_secondary
                            }
                            ult_cost={character.energyUltimate}
                          />
                        )}
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={character.skillsNew.talent.id}
                        maxLevel={character.slug === 'test' ? 8 : 12}
                        element={character.element}
                        gen={character.skillsNew.talent.energy_gen}
                        break_primary={character.skillsNew.talent.break_primary}
                        break_secondary={
                          character.skillsNew.talent.break_secondary
                        }
                        disabled={character.slug === 'test' ? true : false}
                      />
                    </div>
                  </Col>
                  <Col className="forced-width">
                    <div className="box">
                      <HSRSkill
                        id={character.skillsNew.technique.id}
                        maxLevel={1}
                        element={character.element}
                        gen={character.skillsNew.technique.energy_gen}
                        break_primary={
                          character.skillsNew.technique.break_primary
                        }
                        break_secondary={
                          character.skillsNew.technique.break_secondary
                        }
                      />
                    </div>
                  </Col>
                  {character.slug === 'castorice' && (
                    <>
                      <Col className="forced-width">
                        <div className="box">
                          <HSRSkill
                            id={'140706'}
                            maxLevel={1}
                            element={character.element}
                            gen={''}
                            break_primary={''}
                            break_secondary={''}
                            disabled={false}
                          />
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            </>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>skills aren't available yet.</strong> They will be added
                when the character is released.
              </p>
            </div>
          )}
          {character.slug === 'trailblazer-remembrance' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Memesprite Skills
              </div>
              <div className={`skills ${character.element}`}>
                <Row xs={1} xl={2} xxl={2}>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'8007010'}
                        maxLevel={7}
                        element={character.element}
                        gen={'10'}
                        break_primary={'15'}
                        break_secondary={'30'}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'8007011'}
                        maxLevel={7}
                        element={character.element}
                        gen={'10'}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'8007012'}
                        maxLevel={7}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'8007013'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'8007014'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
          {character.slug === 'aglaea' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Memesprite Skills
              </div>
              <div className={`skills ${character.element}`}>
                <Row xs={1} xl={2} xxl={2}>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'1402010'}
                        maxLevel={7}
                        element={character.element}
                        gen={'10'}
                        break_primary={'30'}
                        break_secondary={'15'}
                        disabled={false}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'1402012'}
                        maxLevel={7}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'1402013'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'1402014'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
          {character.slug === 'castorice' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Memesprite Skills
              </div>
              <div className={`skills ${character.element}`}>
                <Row xs={1} xl={2} xxl={2}>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'1407010'}
                        maxLevel={7}
                        element={character.element}
                        gen={''}
                        break_primary={'30'}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'1407011'}
                        maxLevel={7}
                        element={character.element}
                        gen={''}
                        break_primary={'30'}
                        break_secondary={''}
                        disabled={false}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className="box">
                      <HSRSkill
                        id={'1407012'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'1407013'}
                        maxLevel={1}
                        element={character.element}
                        gen={''}
                        break_primary={''}
                        break_secondary={''}
                        disabled={false}
                      />
                      <HSRSkill
                        id={'1407014'}
                        maxLevel={7}
                        element={character.element}
                        gen={''}
                        break_primary={'15'}
                        break_secondary={''}
                        disabled={false}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
          {/* {character.slug === 'aglaea' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Memesprite Skills
              </div>
              <div className="info-box">
                <p>
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  <strong>memesprite skills aren't available yet.</strong> They
                  will be added when the character is released.
                </p>
              </div>
            </>
          )} */}
          <div className={`content-header ${character.element} `}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Major Traces
          </div>
          {character.skillsNew ? (
            <>
              <div className={`skills traces ${character.element}`}>
                <Row xs={1} xl={2} xxl={3}>
                  {character.traces.map((trace, index) => {
                    return (
                      <Col key={index}>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              {trace.req}
                              <span>Major trace</span>
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">{trace.name}</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring ${character.element}`}
                            dangerouslySetInnerHTML={{
                              __html: trace.desc
                            }}
                          />
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>traces aren't available yet.</strong> They will be added
                when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Minor Traces (total)
          </div>
          {character.characterBuilderInfo ? (
            <>
              <div className="smaller-traces">
                <Row xs={1} xxl={3}>
                  <Col>
                    <div className="box trace">
                      <HSRStat
                        stat={character.characterBuilderInfo.traceStat1.stat}
                      />{' '}
                      <span className="value">
                        +{character.characterBuilderInfo.traceStat1.value}%
                      </span>
                    </div>
                  </Col>
                  <Col>
                    <div className="box trace">
                      <HSRStat
                        stat={character.characterBuilderInfo.traceStat2.stat}
                      />{' '}
                      <span className="value">
                        +
                        {character.characterBuilderInfo.traceStat2.stat ===
                        'speed' ? (
                          <>{character.characterBuilderInfo.traceStat2.value}</>
                        ) : (
                          <>
                            {character.characterBuilderInfo.traceStat2.value}%
                          </>
                        )}
                      </span>
                    </div>
                  </Col>
                  <Col>
                    <div className="box trace">
                      <HSRStat
                        stat={character.characterBuilderInfo.traceStat3.stat}
                      />{' '}
                      <span className="value">
                        +
                        {character.characterBuilderInfo.traceStat3.stat ===
                        'Speed' ? (
                          <>{character.characterBuilderInfo.traceStat3.value}</>
                        ) : (
                          <>
                            {character.characterBuilderInfo.traceStat3.value}%
                          </>
                        )}
                      </span>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>traces aren't available yet.</strong> They will be added
                when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Eidolons
          </div>
          {character.characterBuilderInfo ? (
            <>
              <div className="skills eidolons">
                {character.eidolon.map((eidolon, index) => {
                  return (
                    <Row xs={1} xxl={2} key={index}>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E1
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade1Name}
                              </p>
                              <p className="skill-type">Eidolon 1</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade1Desc, options)}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E2
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade2Name}
                              </p>
                              <p className="skill-type">Eidolon 2</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade2Desc, options)}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E3
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade3Name}
                              </p>
                              <p className="skill-type">Eidolon 3</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade3Desc, options)}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E4
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade4Name}
                              </p>
                              <p className="skill-type">Eidolon 4</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade4Desc, options)}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E5
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade5Name}
                              </p>
                              <p className="skill-type">Eidolon 5</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade5Desc, options)}
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <div className="box">
                          <div className="skill-header">
                            <div className={`skill-icon ${character.element}`}>
                              E6
                            </div>
                            <div className="skill-info">
                              <p className="skill-name">
                                {eidolon.upgrade6Name}
                              </p>
                              <p className="skill-type">Eidolon 6</p>
                            </div>
                          </div>
                          <div
                            className={`skill-with-coloring eidolon ${character.element}`}
                          >
                            {renderRichText(eidolon.upgrade6Desc, options)}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>Eidolons aren't available yet.</strong> They will be
                added when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Stats and materials
          </div>
          {character.stats && !character.hideSkills ? (
            <>
              <div className="stats-controls">
                <Select
                  options={levelOption}
                  className="custom-dropdown"
                  isSearchable={false}
                  onChange={levelChangeHandler}
                  defaultValue={defaultLevel}
                  classNamePrefix="custom-dropdown-inner"
                />
                <div className="custom-switch">
                  <Switch
                    checked={enableAscension}
                    onChange={() => setEnableAscension(!enableAscension)}
                    disabled={level === 1 || level === 80}
                    onColor="#009EEC"
                    offColor="#484950"
                    className="switch"
                  />
                  Enable Ascension
                </div>
              </div>
              <Row xs={1} xl={2} xxl={2} className="info-list stats">
                <Col>
                  <div className="stat-box">
                    <h5>Level {level} stats</h5>
                    <div className="info-list-row">
                      <div className="category">
                        <HSRStat stat="hp" />
                      </div>
                      <div className="details">
                        {character.stats.hp_base && (
                          <HSRCalculateStats
                            base={character.stats.hp_base}
                            level={level}
                            enableAscension={enableAscension}
                          />
                        )}
                      </div>
                    </div>
                    <div className="info-list-row">
                      <div className="category">
                        <HSRStat stat="atk" />
                      </div>
                      <div className="details">
                        {character.stats.hp_base && (
                          <HSRCalculateStats
                            base={character.stats.atk_base}
                            level={level}
                            enableAscension={enableAscension}
                          />
                        )}
                      </div>
                    </div>
                    <div className="info-list-row">
                      <div className="category">
                        <HSRStat stat="def" />
                      </div>
                      <div className="details">
                        {character.stats.hp_base && (
                          <HSRCalculateStats
                            base={character.stats.def_base}
                            level={level}
                            enableAscension={enableAscension}
                          />
                        )}
                      </div>
                    </div>
                    <div className="info-list-row">
                      <div className="category">
                        <HSRStat stat="speed" />
                      </div>
                      <div className="details">
                        {character.stats.speed_base}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="stat-box">
                    <h5>
                      Level{' '}
                      {level === 1 || level === 80 ? (
                        <>
                          {level === 1 && level}
                          {level === 80 && <>1 → {level}</>}
                        </>
                      ) : (
                        <>
                          {level} → {level}+
                        </>
                      )}{' '}
                      Ascension Materials
                    </h5>
                    <div className="material-list">
                      {level === 80 ? (
                        <>
                          <ul className="list-unstyled">
                            <li>
                              <HSRItem
                                slug="credit"
                                mode="inline"
                                enablePopover
                              />{' '}
                              {character.rarity === '4' ||
                              character.slug === 'trailblazer-destruction' ||
                              character.slug === 'trailblazer-preservation' ||
                              character.slug === 'trailblazer-harmony' ||
                              character.slug === 'trailblazer-remembrance' ? (
                                <>x 246400</>
                              ) : (
                                <>x 308000</>
                              )}
                            </li>
                            <li>
                              <HSRItem
                                slug={character.ascensionMaterials.mats_1}
                                mode="inline"
                                enablePopover
                              />{' '}
                              {character.rarity === '4' ||
                              character.slug === 'trailblazer-destruction' ||
                              character.slug === 'trailblazer-preservation' ||
                              character.slug === 'trailblazer-harmony' ||
                              character.slug === 'trailblazer-remembrance' ? (
                                <>x 12</>
                              ) : (
                                <>x 15</>
                              )}
                            </li>
                            <li>
                              <HSRItem
                                slug={character.ascensionMaterials.mats_2}
                                mode="inline"
                                enablePopover
                              />{' '}
                              {character.rarity === '4' ||
                              character.slug === 'trailblazer-destruction' ||
                              character.slug === 'trailblazer-preservation' ||
                              character.slug === 'trailblazer-harmony' ||
                              character.slug === 'trailblazer-remembrance' ? (
                                <>x 13</>
                              ) : (
                                <>x 15</>
                              )}
                            </li>
                            <li>
                              <HSRItem
                                slug={character.ascensionMaterials.mats_3}
                                mode="inline"
                                enablePopover
                              />{' '}
                              {character.rarity === '4' ||
                              character.slug === 'trailblazer-destruction' ||
                              character.slug === 'trailblazer-preservation' ||
                              character.slug === 'trailblazer-harmony' ||
                              character.slug === 'trailblazer-remembrance' ? (
                                <>
                                  {character.slug ===
                                    'trailblazer-destruction' ||
                                  character.slug ===
                                    'trailblazer-preservation' ||
                                  character.slug === 'trailblazer-harmony' ||
                                  character.slug === 'trailblazer-remembrance'
                                    ? 'x 28'
                                    : 'x 50'}
                                </>
                              ) : (
                                <>x 65</>
                              )}
                            </li>
                            <li>
                              <HSRItem
                                slug={character.ascensionMaterials.mats_4}
                                mode="inline"
                                enablePopover
                              />{' '}
                              {character.rarity === '4' ||
                              character.slug === 'trailblazer-destruction' ||
                              character.slug === 'trailblazer-preservation' ||
                              character.slug === 'trailblazer-harmony' ||
                              character.slug === 'trailblazer-remembrance' ? (
                                <>x 12</>
                              ) : (
                                <>x 15</>
                              )}
                            </li>
                          </ul>
                        </>
                      ) : (
                        <>
                          {level === 1 ? (
                            <>
                              <p>Ascension isn't available on this level.</p>
                            </>
                          ) : (
                            <ul className="list-unstyled">
                              <li>
                                <HSRItem
                                  slug="credit"
                                  mode="inline"
                                  enablePopover
                                />{' '}
                                {character.rarity === '4' ||
                                character.slug === 'trailblazer-destruction' ||
                                character.slug === 'trailblazer-preservation' ||
                                character.slug === 'trailblazer-harmony' ||
                                character.slug === 'trailblazer-remembrance' ? (
                                  <>
                                    {level === 20 && 'x 3200'}
                                    {level === 30 && 'x 6400'}
                                    {level === 40 && 'x 12800'}
                                    {level === 50 && 'x 32000'}
                                    {level === 60 && 'x 64000'}
                                    {level === 70 && 'x 128000'}
                                  </>
                                ) : (
                                  <>
                                    {level === 20 && 'x 4000'}
                                    {level === 30 && 'x 8000'}
                                    {level === 40 && 'x 16000'}
                                    {level === 50 && 'x 40000'}
                                    {level === 60 && 'x 80000'}
                                    {level === 70 && 'x 160000'}
                                  </>
                                )}
                              </li>
                              <li>
                                {(level === 20 || level === 30) && (
                                  <>
                                    <HSRItem
                                      slug={character.ascensionMaterials.mats_1}
                                      mode="inline"
                                      enablePopover
                                    />{' '}
                                    {character.rarity === '4' ||
                                    character.slug ===
                                      'trailblazer-destruction' ||
                                    character.slug ===
                                      'trailblazer-preservation' ||
                                    character.slug === 'trailblazer-harmony' ||
                                    character.slug ===
                                      'trailblazer-remembrance' ? (
                                      <>
                                        {level === 20 && 'x 4'}
                                        {level === 30 && 'x 8'}
                                      </>
                                    ) : (
                                      <>
                                        {level === 20 && 'x 5'}
                                        {level === 30 && 'x 10'}
                                      </>
                                    )}
                                  </>
                                )}
                                {(level === 40 || level === 50) && (
                                  <>
                                    <HSRItem
                                      slug={character.ascensionMaterials.mats_2}
                                      mode="inline"
                                      enablePopover
                                    />{' '}
                                    {character.rarity === '4' ||
                                    character.slug ===
                                      'trailblazer-destruction' ||
                                    character.slug ===
                                      'trailblazer-preservation' ||
                                    character.slug === 'trailblazer-harmony' ||
                                    character.slug ===
                                      'trailblazer-remembrance' ? (
                                      <>
                                        {level === 40 && 'x 5'}
                                        {level === 50 && 'x 8'}
                                      </>
                                    ) : (
                                      <>
                                        {level === 40 && 'x 6'}
                                        {level === 50 && 'x 9'}
                                      </>
                                    )}
                                  </>
                                )}
                                {(level === 60 || level === 70) && (
                                  <>
                                    <HSRItem
                                      slug={character.ascensionMaterials.mats_4}
                                      mode="inline"
                                      enablePopover
                                    />{' '}
                                    {character.rarity === '4' ||
                                    character.slug ===
                                      'trailblazer-destruction' ||
                                    character.slug ===
                                      'trailblazer-preservation' ||
                                    character.slug === 'trailblazer-harmony' ||
                                    character.slug ===
                                      'trailblazer-remembrance' ? (
                                      <>
                                        {level === 60 && 'x 5'}
                                        {level === 70 && 'x 7'}
                                      </>
                                    ) : (
                                      <>
                                        {level === 60 && 'x 6'}
                                        {level === 70 && 'x 9'}
                                      </>
                                    )}
                                  </>
                                )}
                              </li>
                              {level >= 40 && (
                                <li>
                                  <HSRItem
                                    slug={character.ascensionMaterials.mats_3}
                                    mode="inline"
                                    enablePopover
                                  />{' '}
                                  {character.rarity === '4' ||
                                  character.slug ===
                                    'trailblazer-destruction' ||
                                  character.slug ===
                                    'trailblazer-preservation' ||
                                  character.slug === 'trailblazer-harmony' ||
                                  character.slug ===
                                    'trailblazer-remembrance' ? (
                                    <>
                                      {character.slug ===
                                        'trailblazer-destruction' ||
                                      character.slug ===
                                        'trailblazer-preservation' ||
                                      character.slug ===
                                        'trailblazer-harmony' ||
                                      character.slug ===
                                        'trailblazer-remembrance' ? (
                                        <>
                                          {level === 40 && 'x 4'}
                                          {level === 50 && 'x 6'}
                                          {level === 60 && 'x 8'}
                                          {level === 70 && 'x 10'}
                                        </>
                                      ) : (
                                        <>
                                          {level === 40 && 'x 2'}
                                          {level === 50 && 'x 5'}
                                          {level === 60 && 'x 15'}
                                          {level === 70 && 'x 28'}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {level === 40 && 'x 3'}
                                      {level === 50 && 'x 7'}
                                      {level === 60 && 'x 20'}
                                      {level === 70 && 'x 35'}
                                    </>
                                  )}
                                </li>
                              )}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>Stats and Materials aren't available yet.</strong> They
                will be added when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> CV
          </div>
          <Row xs={1} xxl={4} className="va-section">
            <Col>
              <div className="info-list-row">
                <div className="category">ENG</div>
                <div className="details">
                  {character.voiceActors.en ? character.voiceActors.en : '-'}
                </div>
              </div>
            </Col>
            <Col>
              <div className="info-list-row">
                <div className="category">JPN</div>
                <div className="details">
                  {character.voiceActors.jpn ? character.voiceActors.jpn : '-'}
                </div>
              </div>
            </Col>
            <Col>
              <div className="info-list-row">
                <div className="category">CN</div>
                <div className="details">
                  {character.voiceActors.cn ? character.voiceActors.cn : '-'}
                </div>
              </div>
            </Col>
            <Col>
              <div className="info-list-row">
                <div className="category">KR</div>
                <div className="details">
                  {character.voiceActors.kr ? character.voiceActors.kr : '-'}
                </div>
              </div>
            </Col>
          </Row>
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Gallery
          </div>
          {fullImage ? (
            <GatsbyImage image={fullImage} alt="" className="full-image" />
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>images aren't available yet.</strong> They will be added
                when the character is released.
              </p>
            </div>
          )}
        </div>
        <div
          className={`tab-inside ${currentTab === 'Review' ? 'active' : ''}`}
        >
          <div className={`mobile-header ${character.element}`}>
            <p>
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
              Review
            </p>
          </div>
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Pros & Cons
          </div>
          {!character.isReviewPending ? (
            <div className="section-analysis">
              <Row className="pros-cons">
                <Col sm="12" md="6">
                  <div className="box pros">
                    <h5>Pros</h5>
                    <hr />
                    <div className="raw list">
                      {renderRichText(character.pros, options)}
                    </div>
                  </div>
                </Col>
                <Col sm="12" md="6">
                  <div className="box cons">
                    <h5>Cons</h5>
                    <hr />
                    <div className="raw list">
                      {renderRichText(character.cons, options)}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>pros & cons aren't available yet.</strong> They will be
                added when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Review (By Sushou)
          </div>
          {!character.isReviewPending ? (
            <div className="section-analysis">
              <div className={`review raw`}>
                {character.review ? (
                  <>{renderRichText(character.review, options)}</>
                ) : (
                  <p>Review is pending.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>review isn't available yet.</strong> It will be added
                when the character is released.
              </p>
            </div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Ratings
          </div>
          <div className="info-box tier">
            <p>
              Honkai: Star Rail{' '}
              <strong>is a game where team building matters most</strong> and
              while our tier list takes the optimal setup into account, a lot of
              characters can work and do well - even those ranked lower - when
              you invest into them. Also for story or lower difficulties of
              Simulated Universe, you don't need to worry about ratings and
              tiers. You can safely clear that content even with F2P characters
              as long as you build your team correctly.
            </p>
            {character.rarity === '5' ? (
              <p>
                Character at 5★ are rated at their Eidolon 0 with their best in
                slot <strong>non-limited</strong> Light Cone.
              </p>
            ) : (
              ''
            )}
            <p>Available ratings:</p>
            <ul>
              <li>
                <strong>Memory of Chaos (MoC)</strong> - how the character
                performs in the Memory of Chaos. Blast and single target damage
                are important here while AoE has niche uses against some bosses.
              </li>
              <li>
                <strong>Pure Fiction (PF)</strong> - how the character performs
                in the Pure Fiction. AoE is king here, followed by Blast while
                single target damage is mostly useless.
              </li>
              <li>
                <strong>Apocalyptic Shadow (AS)</strong> - how the character
                performs in the Apocalyptic Shadow. Single target and Break is
                important here.
              </li>
            </ul>
          </div>
          {!character.isReviewPending ? (
            <>
              {HSRRatings.map((char, index) => {
                return (
                  <div key={index}>
                    {char.slug === character.slug && (
                      <>
                        <h5>
                          {char.cat === 'DPS' && (
                            <span className="role dps-2">
                              <FontAwesomeIcon icon={faHandFist} width="12" />{' '}
                              Damage dealer
                            </span>
                          )}
                          {char.cat === 'Specialist' && (
                            <span className="role debuffer">
                              <FontAwesomeIcon icon={faVirus} width="12" />{' '}
                              Support DPS
                            </span>
                          )}
                          {char.cat === 'Buffer' && (
                            <span className="role support">
                              <FontAwesomeIcon icon={faFlask} width="12" />{' '}
                              Amplifier
                            </span>
                          )}
                          {char.cat === 'Sustain' && (
                            <span className="role sustain">
                              <FontAwesomeIcon
                                icon={faBriefcaseMedical}
                                width="12"
                              />{' '}
                              Sustain
                            </span>
                          )}{' '}
                        </h5>
                        {char.rarity === 5 ||
                        char.slug === 'march-7th-swordmaster' ? (
                          <>
                            <div className="ratings-container">
                              <RatingBox
                                game="hsr"
                                rating={char.moc_rating}
                                ratingName="MoC"
                              />
                              <RatingBox
                                game="hsr"
                                rating={char.pure_rating}
                                ratingName="PF"
                              />
                              <RatingBox
                                game="hsr"
                                rating={char.apo_rating}
                                ratingName="AS"
                              />
                            </div>
                          </>
                        ) : (
                          <Row xs={1} xxl={2} className="ratings-4star">
                            <Col>
                              <div className="box">
                                <h5>Eidolon 0</h5>
                                <hr />
                                <div className="ratings-container">
                                  <RatingBox
                                    game="hsr"
                                    rating={char.moc_special_rating}
                                    ratingName="MoC"
                                  />
                                  <RatingBox
                                    game="hsr"
                                    rating={char.pure_special_rating}
                                    ratingName="PF"
                                  />
                                  <RatingBox
                                    game="hsr"
                                    rating={char.apo_special_rating}
                                    ratingName="AS"
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col>
                              <div className="box">
                                <h5>Eidolon 6</h5>
                                <hr />
                                <div className="ratings-container">
                                  <RatingBox
                                    game="hsr"
                                    rating={char.moc_rating}
                                    ratingName="MoC"
                                  />
                                  <RatingBox
                                    game="hsr"
                                    rating={char.pure_rating}
                                    ratingName="PF"
                                  />
                                  <RatingBox
                                    game="hsr"
                                    rating={char.apo_rating}
                                    ratingName="AS"
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="info-box with-margin">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                ratings aren't available yet. They will be added as soon as it's
                possible.
              </p>
            </div>
          )}
        </div>
        <div className={`tab-inside ${currentTab === 'Build' ? 'active' : ''}`}>
          <div className={`mobile-header ${character.element}`}>
            <p>
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
              Build and teams
            </p>
          </div>
          {character.buildData && character.showBuild ? (
            <>
              {character.tierCategory === 'Sustain' ||
              character.tierCategory === 'Amplifier' ||
              character.slug === 'tribbie' ? (
                <>
                  <div className={`section-build ${character.element} `}>
                    <HSRProfileCones
                      slug={character.slug}
                      name={character.name}
                      element={character.element}
                    />
                    <HSRProfileRelics
                      slug={character.slug}
                      name={character.name}
                      element={character.element}
                      path={character.path}
                    />
                    {character.buildData.map((build) => {
                      return (
                        <>
                          <div className="build-stats">
                            <div
                              className={`content-header ${character.element}`}
                            >
                              <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                              Best Stats
                            </div>
                            <Row xs={1} xl={2} xxl={4} className="main-stats">
                              <Col>
                                <div className="box">
                                  <div className="stats-header">
                                    <span>Body</span>
                                  </div>
                                  <div className="list-stats">
                                    {build.body && (
                                      <>
                                        {build.body.map((stat, idx) => {
                                          return (
                                            <>
                                              <HSRStat
                                                key={idx}
                                                stat={stat.stat}
                                              />
                                              {stat.sign && stat.sign != '' && (
                                                <span className="order">
                                                  {stat.sign}
                                                </span>
                                              )}
                                            </>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col>
                                <div className="box">
                                  <div className="stats-header">
                                    <span>Feet</span>
                                  </div>
                                  <div className="list-stats">
                                    {build.feet && (
                                      <>
                                        {build.feet.map((stat, idx) => {
                                          return (
                                            <>
                                              <HSRStat
                                                key={idx}
                                                stat={stat.stat}
                                              />
                                              {stat.sign && stat.sign != '' && (
                                                <span className="order">
                                                  {stat.sign}
                                                </span>
                                              )}
                                            </>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col>
                                <div className="box">
                                  <div className="stats-header">
                                    <span>Planar Sphere</span>
                                  </div>
                                  <div className="list-stats">
                                    {build.sphere && (
                                      <>
                                        {build.sphere.map((stat, idx) => {
                                          return (
                                            <>
                                              <HSRStat
                                                key={idx}
                                                stat={stat.stat}
                                              />
                                              {stat.sign && stat.sign != '' && (
                                                <span className="order">
                                                  {stat.sign}
                                                </span>
                                              )}
                                            </>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col>
                                <div className="box">
                                  <div className="stats-header">
                                    <span>Link Rope</span>
                                  </div>
                                  <div className="list-stats">
                                    {build.rope && (
                                      <>
                                        {build.rope.map((stat, idx) => {
                                          return (
                                            <>
                                              <HSRStat
                                                key={idx}
                                                stat={stat.stat}
                                              />
                                              {stat.sign && stat.sign != '' && (
                                                <span className="order">
                                                  {stat.sign}
                                                </span>
                                              )}
                                            </>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <div className="box sub-stats">
                                  <span>Substats:</span>
                                  <p>{build.substats}</p>
                                </div>
                              </Col>
                            </Row>
                            {character.statsComments && (
                              <>
                                <p className="with-margin">
                                  If you want to learn more about the various
                                  stats and breakpoints, expand the section
                                  below.
                                </p>
                                <Accordion className="default-look">
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                      Details about the Stats
                                    </Accordion.Header>
                                    <Accordion.Body>
                                      <>
                                        {renderRichText(
                                          character.statsComments,
                                          options
                                        )}
                                      </>
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Accordion>
                              </>
                            )}
                            <Row className="stats-comments ">
                              <Col>
                                <div
                                  className={`content-header ${character.element}`}
                                >
                                  <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                  Build comments
                                </div>
                                <p>{build.comments ? build.comments : '-'}</p>
                              </Col>
                            </Row>
                            <div
                              className={`content-header ${character.element}`}
                            >
                              <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                              Recommended endgame stats
                            </div>
                            {character.endgameStats ? (
                              <div>
                                <div className="raw list">
                                  {renderRichText(
                                    character.endgameStats,
                                    options
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="info-box">
                                <p>
                                  <strong className={`${character.element}`}>
                                    {character.name}
                                  </strong>{' '}
                                  <strong>
                                    endgame stats aren't available yet.
                                  </strong>
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="build-stats">
                            <div
                              className={`content-header ${character.element}`}
                            >
                              <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                              Traces priority
                            </div>
                            <Row>
                              <Col>
                                <div className="box sub-stats">
                                  <span>Skills priority:</span>
                                  <p>
                                    {build.skill_priority
                                      ? build.skill_priority
                                      : '-'}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <div className="box sub-stats">
                                  <span>Major Traces priority:</span>
                                  <p>
                                    {build.traces_priority
                                      ? build.traces_priority
                                      : '-'}
                                  </p>
                                </div>
                              </Col>
                            </Row>
                          </div>
                          {character.slug === 'tribbie' && (
                            <>
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Speed Information
                              </div>
                              <TribbieSpeedTuning />
                            </>
                          )}
                          {(character.slug === 'sparkle' ||
                            character.slug === 'bronya' ||
                            character.slug === 'sunday') && (
                            <>
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Sparkle vs Bronya vs Sunday
                              </div>
                              <div className="team-container-outside">
                                <HSRCharacter
                                  mode="icon"
                                  slug="sparkle"
                                  enablePopover
                                />
                                <HSRCharacter
                                  mode="icon"
                                  slug="bronya"
                                  enablePopover
                                />
                                <HSRCharacter
                                  mode="icon"
                                  slug="sunday"
                                  enablePopover
                                />
                              </div>
                              <p>
                                If you want to learn how{' '}
                                <strong className="Quantum">Sparkle</strong>{' '}
                                compares to{' '}
                                <strong className="Wind">Bronya</strong> and{' '}
                                <strong className="Imaginary">Sunday</strong>,
                                check the <strong>Calculations</strong> tab for
                                a very detailed analysis.
                              </p>
                            </>
                          )}
                          {(character.slug === 'trailblazer-harmony' ||
                            character.slug === 'tingyun-fugue') && (
                            <>
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Harmony MC vs Fugue
                              </div>
                              <div className="employee-container for-nikke">
                                <HSRCharacter
                                  mode="icon"
                                  slug="tingyun-fugue"
                                  enablePopover
                                />
                                <HSRCharacter
                                  mode="icon"
                                  slug="trailblazer-harmony"
                                  enablePopover
                                />
                              </div>
                              <p>
                                We have created a separate guide that compares{' '}
                                <strong className="Fire">Fugue</strong> with{' '}
                                <strong className="Imaginary">
                                  Harmony Trailblazer
                                </strong>{' '}
                                who is her direct competitor in the Break
                                support role. Check the calculations tab for
                                more details.
                              </p>
                            </>
                          )}
                        </>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <Tabs
                    defaultActiveKey="0"
                    id="build-tabs"
                    className={`build-tab ${character.element} ${
                      character.buildData.length > 1
                        ? 'more-build'
                        : 'single-build'
                    }`}
                  >
                    {character.buildData.map((build, index) => {
                      return (
                        <Tab key={index} eventKey={index} title={build.name}>
                          <div
                            className={`section-build ${character.element} `}
                          >
                            <div className="build-cones">
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Best Light Cones
                              </div>
                              {character.path === 'Erudition' && (
                                <>
                                  <div className="info-box with-margin">
                                    <p>
                                      <strong
                                        className={`${character.element}`}
                                      >
                                        {character.name}
                                      </strong>{' '}
                                      Relic and Light Cone calculations are
                                      based on fighting against 3 targets.
                                    </p>
                                  </div>
                                </>
                              )}
                              <div>
                                {character.conesNew && (
                                  <>
                                    <p>
                                      The numbers show the percentage damage
                                      output increase/decrease compared to the
                                      Light Cone marked with 100%.
                                    </p>
                                    {character.slug ===
                                    'march-7th-swordmaster' ? (
                                      <>
                                        {index === 0 ? (
                                          <>
                                            {character.conesNew.map(
                                              (coneNew, idx) => {
                                                return (
                                                  <div
                                                    key={idx}
                                                    className="detailed-cones moc"
                                                  >
                                                    <div
                                                      className={`single-cone ${character.element}`}
                                                    >
                                                      {coneNew.percent != '-' &&
                                                        coneNew.percent !=
                                                          '' && (
                                                          <div className="percentage">
                                                            <p>
                                                              {coneNew.percent}
                                                            </p>
                                                          </div>
                                                        )}
                                                      <HSRLightCone
                                                        slug={coneNew.cone}
                                                        mode="profile"
                                                        superImp={coneNew.super}
                                                      />
                                                    </div>
                                                    {coneNew.notes != '' &&
                                                      coneNew.notes != '-' && (
                                                        <div
                                                          className={`information ${character.element}`}
                                                          dangerouslySetInnerHTML={{
                                                            __html:
                                                              coneNew.notes
                                                          }}
                                                        />
                                                      )}
                                                  </div>
                                                );
                                              }
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            <p>
                                              Those are ADDITIONAL options for
                                              BREAK March. She can still use the
                                              CRIT Cones to pick up some CRIT
                                              scaling if desired - it may even
                                              lead to better performance in
                                              combat.
                                            </p>
                                            <div className="detailed-cones moc">
                                              <div
                                                className={`single-cone ${character.element}`}
                                              >
                                                <div className="percentage">
                                                  <p>1</p>
                                                </div>
                                                <HSRLightCone
                                                  slug="sailing-towards-a-second-life"
                                                  mode="profile"
                                                  superImp="1"
                                                />
                                              </div>
                                              <div className="information">
                                                <p>
                                                  Best Cone option for Break
                                                  March, being one of the only
                                                  Hunt Cones with Break Effect %
                                                  and break scaling effects
                                                  while also offering a
                                                  tremendous amount of Speed %.
                                                </p>
                                              </div>
                                            </div>
                                            <div className="detailed-cones moc">
                                              <div
                                                className={`single-cone ${character.element}`}
                                              >
                                                <div className="percentage">
                                                  <p>2</p>
                                                </div>
                                                <HSRLightCone
                                                  slug="adversarial"
                                                  mode="profile"
                                                  superImp="5"
                                                />
                                              </div>
                                              <div className="information">
                                                <p>
                                                  F2P option for Break March's
                                                  who are only interested in
                                                  dealing as much Break damage
                                                  as possible and find the speed
                                                  bonus of this Cone worth
                                                  trading in any large potential
                                                  bonuses to her standard
                                                  abilities damage.
                                                </p>
                                              </div>
                                            </div>
                                            <div className="detailed-cones moc">
                                              <div
                                                className={`single-cone ${character.element}`}
                                              >
                                                <div className="percentage">
                                                  <p>3</p>
                                                </div>
                                                <HSRLightCone
                                                  slug="river-flows-in-spring"
                                                  mode="profile"
                                                  superImp="5"
                                                />
                                              </div>
                                              <div className="information">
                                                <p>
                                                  Additional F2P option to
                                                  Adversarial for Break March
                                                  with a different conditional
                                                  bonus sometimes being easier
                                                  or harder to maintain in
                                                  battles. Offers less Speed but
                                                  grants some DMG% to make up
                                                  for it.
                                                </p>
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {character.conesNew.map(
                                          (coneNew, idx) => {
                                            return (
                                              <div
                                                key={idx}
                                                className="detailed-cones moc"
                                              >
                                                <div
                                                  className={`single-cone ${character.element}`}
                                                >
                                                  {coneNew.percent != '-' &&
                                                    coneNew.percent != '' && (
                                                      <div className="percentage">
                                                        <p>{coneNew.percent}</p>
                                                      </div>
                                                    )}
                                                  <HSRLightCone
                                                    slug={coneNew.cone}
                                                    mode="profile"
                                                    superImp={coneNew.super}
                                                  />
                                                </div>
                                                {coneNew.notes != '' &&
                                                  coneNew.notes != '-' && (
                                                    <div
                                                      className={`information ${character.element}`}
                                                      dangerouslySetInnerHTML={{
                                                        __html: coneNew.notes
                                                      }}
                                                    />
                                                  )}
                                              </div>
                                            );
                                          }
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="build-relics">
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Best Relics
                              </div>
                              {character.path === 'Erudition' && (
                                <>
                                  <div className="info-box with-margin">
                                    <p>
                                      <strong
                                        className={`${character.element}`}
                                      >
                                        {character.name}
                                      </strong>{' '}
                                      Relic and Light Cone calculations are
                                      based on fighting against 3 targets.
                                    </p>
                                  </div>
                                </>
                              )}
                              {character.path === 'Himeko' && (
                                <>
                                  <div className="info-box with-margin">
                                    <p>
                                      If you're playing
                                      <strong
                                        className={`${character.element}`}
                                      >
                                        {character.name}
                                      </strong>{' '}
                                      as Super Breaker, follow Firefly Relic
                                      suggestions instead.
                                    </p>
                                  </div>
                                </>
                              )}
                              {character.slug === 'castorice' && (
                                <>
                                  <div className="info-box with-margin">
                                    <p>
                                      Keep in mind that majority of{' '}
                                      <strong
                                        className={`${character.element}`}
                                      >
                                        {character.name}
                                      </strong>{' '}
                                      damage comes from Netherwing - most set
                                      effects don't really work or do anything
                                      to benefit her.
                                    </p>
                                  </div>
                                </>
                              )}
                              <p>
                                The percentage numbers showcase the relative DPS
                                increase when you take into account the set
                                bonus only. For some characters there's no DPS
                                (or its equivalent) available and in that case
                                we listed the sets in the order of priority.
                              </p>
                              <h6>Best Relic Sets</h6>
                              <div className="detailed-cones moc extra planar">
                                {build.relics && (
                                  <>
                                    {build.relics.map((relic, idx) => {
                                      return (
                                        <>
                                          <div
                                            className={`single-cone ${
                                              relic.notes || relic.special
                                                ? 'with-notes'
                                                : ''
                                            } ${character.element}`}
                                            key={idx}
                                          >
                                            <div
                                              className={`percentage ${
                                                relic.comment
                                                  ? 'extended'
                                                  : 'default'
                                              }`}
                                            >
                                              <p>
                                                {relic.value ? (
                                                  <>{relic.value}</>
                                                ) : (
                                                  <>{idx + 1}</>
                                                )}
                                              </p>
                                              {relic.comment && (
                                                <>
                                                  <span>{relic.comment}</span>
                                                </>
                                              )}
                                            </div>
                                            {relic.relic_2 != '' ? (
                                              <div className="double-set">
                                                <HSRRelicSet
                                                  name={relic.relic}
                                                  compactMode
                                                  comment="2PC"
                                                />
                                                {relic.relic_2 === 'Flex' ? (
                                                  <>
                                                    <div className="flex-placeholder">
                                                      Flex
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <HSRRelicSet
                                                      name={relic.relic_2}
                                                      compactMode
                                                      comment="2PC"
                                                    />
                                                  </>
                                                )}
                                              </div>
                                            ) : (
                                              <>
                                                <HSRRelicSet
                                                  name={relic.relic}
                                                  compactMode
                                                  comment="4PC"
                                                />
                                              </>
                                            )}
                                          </div>
                                          {relic.notes && (
                                            <div
                                              className={`information ${character.element}`}
                                              dangerouslySetInnerHTML={{
                                                __html: relic.notes
                                              }}
                                            />
                                          )}
                                          {relic.special && (
                                            <>
                                              <div
                                                className={`information ${character.element}`}
                                              >
                                                <p>
                                                  If you can't put together a
                                                  top recommended 4P set with
                                                  satisfactory sub/main stats,
                                                  mix and match the 2P Bonuses
                                                  from the following instead:
                                                </p>
                                                <ul className="with-sets">
                                                  {relic.special.map(
                                                    (special, idx2) => {
                                                      return (
                                                        <>
                                                          <li key={idx2}>
                                                            <HSRRelicSet
                                                              name={
                                                                special.name
                                                              }
                                                              compactMode
                                                              smol
                                                            />{' '}
                                                            <span className="value">
                                                              {(special.name ===
                                                                'Musketeer of Wild Wheat' ||
                                                                special.name ===
                                                                  'Prisoner in Deep Confinement' ||
                                                                special.name ===
                                                                  'The Wind-Soaring Valorous' ||
                                                                special.name ===
                                                                  'Hero of Triumphant Song') && (
                                                                <>[ATK +12%]</>
                                                              )}
                                                              {(special.name ===
                                                                'Messenger Traversing Hackerspace' ||
                                                                special.name ===
                                                                  "Sacerdos' Relived Ordeal") && (
                                                                <>[SPD +6%]</>
                                                              )}
                                                              {special.name ===
                                                                'Scholar Lost in Erudition' && (
                                                                <>
                                                                  [CRIT Rate
                                                                  +8%]
                                                                </>
                                                              )}
                                                              {(special.name ===
                                                                'Iron Cavalry Against the Scourge' ||
                                                                special.name ===
                                                                  'Thief of Shooting Meteor') && (
                                                                <>
                                                                  [Break Effect
                                                                  +16%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Pioneer Diver of Dead Waters' && (
                                                                <>
                                                                  [DMG to
                                                                  debuffed
                                                                  enemies +12%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Longevous Disciple' && (
                                                                <>[HP +12%]</>
                                                              )}
                                                              {special.name ===
                                                                'The Ashblazing Grand Duke' && (
                                                                <>
                                                                  [FUA DMG +20%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Eagle of Twilight Line"' && (
                                                                <>
                                                                  [Wind DMG
                                                                  +10%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Hunter of Glacial Forest' && (
                                                                <>
                                                                  [Ice DMG +10%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Champion of Streetwise Boxing' && (
                                                                <>
                                                                  [Physical DMG
                                                                  +10%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Genius of Brilliant Stars' && (
                                                                <>
                                                                  [Quantum DMG
                                                                  +10%]
                                                                </>
                                                              )}
                                                              {special.name ===
                                                                'Band of Sizzling Thunder' && (
                                                                <>
                                                                  [Lightning DMG
                                                                  +10%]
                                                                </>
                                                              )}{' '}
                                                            </span>
                                                            {idx2 === 0 &&
                                                              '(Recommended)'}
                                                          </li>
                                                        </>
                                                      );
                                                    }
                                                  )}
                                                </ul>
                                              </div>
                                            </>
                                          )}
                                        </>
                                      );
                                    })}
                                  </>
                                )}
                              </div>
                              {character.slug === 'the-herta' && (
                                <>
                                  <h6>Special Relic Sets</h6>
                                  <div className="detailed-cones moc extra planar">
                                    <div className={`single-cone Ice`}>
                                      <HSRRelicSet
                                        name="Poet of Mourning Collapse"
                                        compactMode
                                        comment="4PC"
                                      />
                                    </div>
                                    <div className="information">
                                      <p>
                                        <strong>
                                          A usable set on The Herta but only
                                          under very strict conditions.
                                        </strong>{' '}
                                        Requires the wearer to build no/minimum
                                        Speed on relics and NOT to allocate The
                                        Herta's Speed stat Traces. This allows
                                        The Herta to enter the battle with
                                        &lt;95 Speed fully activating the 32%
                                        CRIT RATE bonus. With this combo, The
                                        Herta can use ATK% boots and also
                                        reclaim some of her lost Speed in combat
                                        through teaming up with Jade who offers
                                        30. This set is obscure and very niche
                                        but for some min-maxers may be of
                                        interest over more traditional options.
                                        It is only better if you build
                                        incredibly specifically around it.
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                              <h6 className="with-margin">
                                Best Planetary Sets
                              </h6>
                              <div className="detailed-cones moc extra planar">
                                {build.planars && (
                                  <>
                                    {build.planars.map((planar, idx) => {
                                      return (
                                        <>
                                          <div
                                            className={`single-cone ${
                                              planar.notes ? 'with-notes' : ''
                                            } ${character.element}`}
                                            key={idx}
                                          >
                                            <div
                                              className={`percentage ${
                                                planar.comment
                                                  ? 'extended'
                                                  : 'default'
                                              }`}
                                            >
                                              <p>
                                                {planar.value ? (
                                                  <>{planar.value}</>
                                                ) : (
                                                  <>{idx + 1}</>
                                                )}
                                              </p>
                                              {planar.comment && (
                                                <>
                                                  <span>{planar.comment}</span>
                                                </>
                                              )}
                                            </div>
                                            <HSRRelicSet
                                              name={planar.planar}
                                              compactMode
                                            />
                                          </div>
                                          {planar.notes && (
                                            <div
                                              className={`information ${character.element}`}
                                              dangerouslySetInnerHTML={{
                                                __html: planar.notes
                                              }}
                                            />
                                          )}
                                        </>
                                      );
                                    })}
                                  </>
                                )}
                              </div>
                              {character.slug === 'the-herta' && (
                                <>
                                  <h6 className="with-margin">
                                    Special Planetary Sets
                                  </h6>
                                  <div className="detailed-cones moc extra planar">
                                    <div
                                      className={`single-cone with-notes Ice`}
                                    >
                                      <div className={`percentage`}>
                                        <p>101.88%</p>
                                      </div>
                                      <HSRRelicSet
                                        name="Sigonia, the Unclaimed Desolation"
                                        compactMode
                                      />
                                    </div>
                                    <div className={`information Ice`}>
                                      <p>
                                        Alternative to Izumo that has a ramp-up
                                        time as you need to kill 10 enemies to
                                        get the full benefit which will be easy
                                        to do in Pure Fiction, but harder in
                                        other modes, making the set not as
                                        generic as Izumo - the calculations
                                        assume it's fully stacked.
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="build-stats">
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Best Stats
                              </div>
                              <Row xs={1} xl={2} xxl={4} className="main-stats">
                                <Col>
                                  <div className="box">
                                    <div className="stats-header">
                                      <span>Body</span>
                                    </div>
                                    <div className="list-stats">
                                      {build.body && (
                                        <>
                                          {build.body.map((stat, idx) => {
                                            return (
                                              <>
                                                <HSRStat
                                                  key={idx}
                                                  stat={stat.stat}
                                                />
                                                {stat.sign &&
                                                  stat.sign != '' && (
                                                    <span className="order">
                                                      {stat.sign}
                                                    </span>
                                                  )}
                                              </>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                                <Col>
                                  <div className="box">
                                    <div className="stats-header">
                                      <span>Feet</span>
                                    </div>
                                    <div className="list-stats">
                                      {build.feet && (
                                        <>
                                          {build.feet.map((stat, idx) => {
                                            return (
                                              <>
                                                <HSRStat
                                                  key={idx}
                                                  stat={stat.stat}
                                                />
                                                {stat.sign &&
                                                  stat.sign != '' && (
                                                    <span className="order">
                                                      {stat.sign}
                                                    </span>
                                                  )}
                                              </>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                                <Col>
                                  <div className="box">
                                    <div className="stats-header">
                                      <span>Planar Sphere</span>
                                    </div>
                                    <div className="list-stats">
                                      {build.sphere && (
                                        <>
                                          {build.sphere.map((stat, idx) => {
                                            return (
                                              <>
                                                <HSRStat
                                                  key={idx}
                                                  stat={stat.stat}
                                                />
                                                {stat.sign &&
                                                  stat.sign != '' && (
                                                    <span className="order">
                                                      {stat.sign}
                                                    </span>
                                                  )}
                                              </>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                                <Col>
                                  <div className="box">
                                    <div className="stats-header">
                                      <span>Link Rope</span>
                                    </div>
                                    <div className="list-stats">
                                      {build.rope && (
                                        <>
                                          {build.rope.map((stat, idx) => {
                                            return (
                                              <>
                                                <HSRStat
                                                  key={idx}
                                                  stat={stat.stat}
                                                />
                                                {stat.sign &&
                                                  stat.sign != '' && (
                                                    <span className="order">
                                                      {stat.sign}
                                                    </span>
                                                  )}
                                              </>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              {character.slug === 'boothill' && (
                                <p>
                                  The <strong>'Anything'</strong> main stat
                                  means that you should prioritize the sub-stats
                                  first. Boothill doesn't need CRIT DMG or CRIT
                                  Rate and it only improves his damage by 1-3%
                                  as he mainly deals Break damage. So focus on
                                  sub-stats first for those two pieces and only
                                  worry about main stats later. For priority
                                  order, check the{' '}
                                  <strong>Details about the Stats</strong>{' '}
                                  section below.
                                </p>
                              )}
                              <Row>
                                <Col>
                                  <div className="box sub-stats">
                                    <span>Substats:</span>
                                    <p>{build.substats}</p>
                                  </div>
                                </Col>
                              </Row>
                              {character.statsComments && (
                                <>
                                  <p className="with-margin">
                                    If you want to learn more about the various
                                    stats and breakpoints, expand the section
                                    below.
                                  </p>
                                  <Accordion className="default-look">
                                    <Accordion.Item eventKey="0">
                                      <Accordion.Header>
                                        Details about the Stats
                                      </Accordion.Header>
                                      <Accordion.Body>
                                        <>
                                          {renderRichText(
                                            character.statsComments,
                                            options
                                          )}
                                        </>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </Accordion>
                                </>
                              )}
                              <Row className="stats-comments ">
                                <Col>
                                  <div
                                    className={`content-header ${character.element}`}
                                  >
                                    <FontAwesomeIcon
                                      icon={faSquare}
                                      width="18"
                                    />{' '}
                                    Build comments
                                  </div>
                                  <p>{build.comments ? build.comments : '-'}</p>
                                </Col>
                              </Row>
                              {character.slug === 'aventurine' && (
                                <AveProfileStats />
                              )}
                              {character.slug === 'yunli' && (
                                <ProfileYunliTaunt />
                              )}
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Recommended endgame stats
                              </div>
                              {character.endgameStats ? (
                                <div>
                                  <div className="raw list">
                                    {renderRichText(
                                      character.endgameStats,
                                      options
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="info-box">
                                  <p>
                                    <strong className={`${character.element}`}>
                                      {character.name}
                                    </strong>{' '}
                                    <strong>
                                      endgame stats aren't available yet.
                                    </strong>
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="build-stats">
                              <div
                                className={`content-header ${character.element}`}
                              >
                                <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                Traces priority
                              </div>
                              <Row>
                                <Col>
                                  <div className="box sub-stats">
                                    <span>Skills priority:</span>
                                    <p>
                                      {build.skill_priority
                                        ? build.skill_priority
                                        : '-'}
                                    </p>
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <div className="box sub-stats">
                                    <span>Major Traces priority:</span>
                                    <p>
                                      {build.traces_priority
                                        ? build.traces_priority
                                        : '-'}
                                    </p>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                            {character.slug === 'aglaea' && (
                              <>
                                <div
                                  className={`content-header ${character.element}`}
                                >
                                  <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                  Speed Tuning Information
                                </div>
                                <AglaeaSpeedTuning />
                              </>
                            )}
                            {character.slug === 'castorice' && (
                              <>
                                <div
                                  className={`content-header ${character.element}`}
                                >
                                  <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                  Investment
                                </div>
                                <ProfileCastoInvest />
                              </>
                            )}
                            {character.slug === 'firefly' && (
                              <>
                                <div
                                  className={`content-header ${character.element}`}
                                >
                                  <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                  Investment
                                </div>
                                <ProfileFireflyInvest />
                              </>
                            )}
                            {character.slug === 'lingsha' && (
                              <>
                                <div
                                  className={`content-header ${character.element}`}
                                >
                                  <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                                  Lingsha vs Gallagher
                                </div>
                                <ProfileLingshaTable />
                              </>
                            )}
                          </div>
                        </Tab>
                      );
                    })}
                  </Tabs>
                </>
              )}
            </>
          ) : (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Best build
              </div>{' '}
              <div className="info-box">
                <p>
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  <strong>build information aren't available yet.</strong> They
                  will be added when the character is released.
                </p>
              </div>
            </>
          )}
          {character.slug === 'robin' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Tips and Tricks
              </div>
              <ProfileRobinTricks />
            </>
          )}
          {character.slug === 'trailblazer-harmony' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Tips and Tricks
              </div>
              <ProfileHarmonyTricks />
            </>
          )}
          <div className={`content-header ${character.element} `}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Synergy
          </div>
          {character.isReleased ? (
            <>
              <ProfileSynergies slug={character.slug} />
            </>
          ) : (
            <>
              <div className="info-box">
                <p>
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  <strong>synergies aren't available yet.</strong> They will be
                  added when the character is released.
                </p>
              </div>
            </>
          )}
          {character.slug === 'mydei' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Teams
              </div>
              <p>
                Sunday and Tribbie are Mydei's best supports, but if you don't
                have one or the other (or either) available, mixing and matching
                any of the other supportive options with each other is viable.
              </p>
              <div className={`character-teams box Imaginary`}>
                <div className="team-header">
                  <p>Best Current Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="mydei" enablePopover />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="sunday" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="tribbie"
                          enablePopover
                        />
                      </div>
                      <div className="role specialist">
                        <FontAwesomeIcon icon={faVirus} width="18" /> Support
                        DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main multi-equal">
                        <HSRCharacter mode="icon" slug="huohuo" enablePopover />
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>Alternative Supports</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="mydei" enablePopover />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="sparkle"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main multi-equal">
                        <HSRCharacter
                          mode="icon"
                          slug="ruan-mei"
                          enablePopover
                        />
                        <HSRCharacter
                          mode="icon"
                          slug="jiaoqiu"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main multi-equal">
                        <HSRCharacter mode="icon" slug="huohuo" enablePopover />
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>F2P Supports Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="mydei" enablePopover />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="trailblazer-remembrance"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="pela" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main multi-equal">
                        <HSRCharacter mode="icon" slug="huohuo" enablePopover />
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>Sustainless Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="mydei" enablePopover />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="sunday" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="tribbie"
                          enablePopover
                        />
                      </div>
                      <div className="role specialist">
                        <FontAwesomeIcon icon={faVirus} width="18" /> Support
                        DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="trailblazer-remembrance"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {character.slug === 'castorice' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Teams
              </div>
              <div className="info-box with-margin">
                <p>
                  If a column has multiple characters, the{' '}
                  <strong>
                    bigger one is the priority choice for the team
                  </strong>{' '}
                  while the smaller ones are alternatives. If they are the same
                  size, either of them can be used.
                </p>
              </div>
              <div className={`character-teams box Quantum`}>
                <div className="team-header">
                  <p>Best Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="castorice"
                          enablePopover
                        />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="tribbie"
                          enablePopover
                        />
                      </div>
                      <div className="role specialist">
                        <FontAwesomeIcon icon={faVirus} width="18" /> Support
                        DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character multi small">
                        <HSRCharacter
                          mode="icon"
                          slug="trailblazer-remembrance"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="sunday" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character multi-equal small">
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>Tribbie-less Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="castorice"
                          enablePopover
                        />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="ruan-mei"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character multi small">
                        <HSRCharacter
                          mode="icon"
                          slug="trailblazer-remembrance"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="sunday" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character multi-equal small">
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>MyRice Team (Dual DPS)</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="castorice"
                          enablePopover
                        />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="mydei" enablePopover />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="tribbie"
                          enablePopover
                        />
                      </div>
                      <div className="role specialist">
                        <FontAwesomeIcon icon={faVirus} width="18" /> Support
                        DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character multi-equal small">
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                        <HSRCharacter mode="icon" slug="luocha" enablePopover />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
                <div className="team-header">
                  <p>F2P Team</p>
                </div>
                <div className="single-team">
                  <div className="team-row">
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="castorice"
                          enablePopover
                        />
                      </div>
                      <div className="role dps">
                        <FontAwesomeIcon icon={faHandFist} width="18" /> DPS
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter mode="icon" slug="pela" enablePopover />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="trailblazer-remembrance"
                          enablePopover
                        />
                      </div>
                      <div className="role amplifier">
                        <FontAwesomeIcon icon={faFlask} width="18" /> Amplifier
                      </div>
                    </div>
                    <div className="with-role">
                      <div className="character main">
                        <HSRCharacter
                          mode="icon"
                          slug="gallagher"
                          enablePopover
                        />
                      </div>
                      <div className="role sustain">
                        <FontAwesomeIcon icon={faBriefcaseMedical} width="18" />{' '}
                        Sustain
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Teams (MoC)
          </div>
          <p>
            The teams in the section are generated based on the current{' '}
            <strong>Memory of Chaos</strong> data we have gathered. If you want
            to see more statistics about the mode, check the MoC/PF/AS
            Statistics tab.
          </p>
          {items.length === 0 ? (
            <>
              <div className="info-box">
                <p>
                  There are no teams featuring{' '}
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  who have cleared Memory of Chaos in the current cycle.
                </p>
              </div>
            </>
          ) : (
            <div className="team-container-moc">{items}</div>
          )}
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> Video guides
          </div>
          {character.videos ? (
            <Row xs={1} xl={3} className="video-row">
              {character.videos.map((videoLink, idx) => {
                return (
                  <Col key={idx}>
                    <YoutubeEmbed embedId={videoLink.video} />
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="info-box">
              <p>
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                <strong>guide videos aren't available yet.</strong>
              </p>
            </div>
          )}
        </div>
        <div className={`tab-inside ${currentTab === 'MoC' ? 'active' : ''}`}>
          <div className={`mobile-header ${character.element}`}>
            <p>
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
              MoC/PF/AS Statistics
            </p>
          </div>
          <div className={`content-header ${character.element}`}>
            <FontAwesomeIcon icon={faSquare} width="18" /> MoC 3.1 / PF 3.1 / AS
            3.1
          </div>
          {character.isReleased === true &&
          character.slug != 'trailblazer-destruction' &&
          character.slug != 'castorice' ? (
            <>
              <p>
                This section contains information about{' '}
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                performance in the latest <strong>Memory of Chaos</strong> cycle
                (3.1), latest <strong>Pure Fiction</strong> (3.1) and the latest{' '}
                <strong>Apocalyptic Shadow</strong> (3.1), and the commonly used
                Relics and Cones in both modes. The page has been last updated
                on <strong>06.04.2025</strong>.
              </p>
              <ul>
                <li>
                  For MoC the data below is based on <strong>16553</strong>{' '}
                  accounts (7580 random we have scanned and 8973 self-reported).
                </li>
                <li>
                  For PF the data below is based on <strong>18652</strong>{' '}
                  accounts (8625 random we have scanned and 10027
                  self-reported).
                </li>
                <li>
                  For AS the data below is based on <strong>19678</strong>{' '}
                  accounts (9215 random we have scanned and 10463
                  self-reported).
                </li>
              </ul>
              <p>
                If you want to share your clear information with us, you simply
                need to fill the form below and{' '}
                <strong>
                  provide us with your UID and make your Hoyo profile public
                </strong>{' '}
                - we will automatically parse the MoC/PF clear information from
                your profile after that!
              </p>
              <Button
                variant="primary"
                href="https://bit.ly/mocstats"
                target="_blank"
                rel="noreferrer"
                className="custom-button"
              >
                MoC/PF/AS Stats Form
              </Button>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Usage rate (MoC
                12)
              </div>
              <p>
                Characters are ranked with appearance rate, which is how often a
                character was used in Memory of Chaos by the players who we have
                scanned, regardless of whether they own the character or not.{' '}
                <strong>Ownership rate currently cannot be obtained.</strong>
              </p>
              <div className={`moc-data-grid`}>
                {mocData
                  .sort((a, b) =>
                    b.current_app_rate > a.current_app_rate ? 1 : -1
                  )
                  .map((mocCharacter, index) => {
                    return (
                      <>
                        {character.slug === mocCharacter.char && (
                          <div
                            key={index}
                            className={`char-box ${
                              character.slug === mocCharacter.char
                                ? 'current'
                                : ''
                            }`}
                          >
                            <Card className="avatar-card">
                              <HSRCharacter
                                slug={mocCharacter.char}
                                mode="icon"
                                enablePopover
                              />
                            </Card>
                            <div className="column percentage">
                              <p>{mocCharacter.current_app_rate}%</p>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })}
              </div>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Usage rate (PF 4)
              </div>
              <p>
                Characters are ranked with appearance rate, which is how often a
                character was used in Pure Fiction by the players who we have
                scanned, regardless of whether they own the character or not.{' '}
                <strong>Ownership rate currently cannot be obtained.</strong>
              </p>
              <div className={`moc-data-grid`}>
                {pfData
                  .sort((a, b) =>
                    b.current_app_rate > a.current_app_rate ? 1 : -1
                  )
                  .map((mocCharacter, index) => {
                    return (
                      <>
                        {character.slug === mocCharacter.char && (
                          <div
                            key={index}
                            className={`char-box ${
                              character.slug === mocCharacter.char
                                ? 'current'
                                : ''
                            }`}
                          >
                            <Card className="avatar-card">
                              <HSRCharacter
                                slug={mocCharacter.char}
                                mode="icon"
                                enablePopover
                              />
                            </Card>
                            <div className="column percentage">
                              <p>{mocCharacter.current_app_rate}%</p>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })}
              </div>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Usage rate (AS 4)
              </div>
              <p>
                Characters are ranked with appearance rate, which is how often a
                character was used in Apocalyptic Shadow by the players who we
                have scanned, regardless of whether they own the character or
                not.{' '}
                <strong>Ownership rate currently cannot be obtained.</strong>
              </p>
              <div className={`moc-data-grid`}>
                {asData
                  .sort((a, b) =>
                    b.current_app_rate > a.current_app_rate ? 1 : -1
                  )
                  .map((mocCharacter, index) => {
                    return (
                      <>
                        {character.slug === mocCharacter.char && (
                          <div
                            key={index}
                            className={`char-box ${
                              character.slug === mocCharacter.char
                                ? 'current'
                                : ''
                            }`}
                          >
                            <Card className="avatar-card">
                              <HSRCharacter
                                slug={mocCharacter.char}
                                mode="icon"
                                enablePopover
                              />
                            </Card>
                            <div className="column percentage">
                              <p>{mocCharacter.current_app_rate}%</p>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })}
              </div>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Common partners
                (MoC 12)
              </div>
              <p>
                This section lists the characters that were most commonly paired
                with{' '}
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                in the current phase of Memory of Chaos.
              </p>
              {MoCDuos.filter(
                (char) => char.char.toLowerCase() === character.slug
              ).map((duo, idx) => {
                return (
                  <div key={idx} className="moc-duo on-moc">
                    <div className={`single-char`}>
                      <Card className="avatar-card">
                        <HSRCharacter
                          slug={duo.char_1.toLowerCase()}
                          mode="icon"
                          enablePopover
                        />
                      </Card>
                      <div className="column percentage">
                        <p>{duo.app_rate_1}</p>
                      </div>
                    </div>
                    {duo.char_2 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_2.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_2}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_3 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_3.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_3}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_4 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_4.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_4}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_5 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_5.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_5}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_6 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_6.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_6}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_7 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_7.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_7}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_8 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_8.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_8}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* {character.slug === 'rappa' && (
                <div className="info-box">
                  <p>
                    The partners for{' '}
                    <strong className={`${character.element}`}>
                      {character.name}
                    </strong>{' '}
                    aren't available yet, because there's no data for Memory of
                    Chaos including the character yet.
                  </p>
                </div>
              )} */}
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Common partners
                (PF)
              </div>
              <p>
                This section lists the characters that were most commonly paired
                with{' '}
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                in the current phase of Pure Fiction.
              </p>
              {PFDuos.filter(
                (char) => char.char.toLowerCase() === character.slug
              ).map((duo, idx) => {
                return (
                  <div key={idx} className="moc-duo on-moc ">
                    <div className={`single-char`}>
                      <Card className="avatar-card">
                        <HSRCharacter
                          slug={duo.char_1.toLowerCase()}
                          mode="icon"
                          enablePopover
                        />
                      </Card>
                      <div className="column percentage">
                        <p>{duo.app_rate_1}</p>
                      </div>
                    </div>
                    {duo.char_2 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_2.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_2}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_3 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_3.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_3}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_4 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_4.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_4}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_5 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_5.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_5}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_6 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_6.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_6}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_7 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_7.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_7}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_8 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_8.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_8}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* {character.slug === 'rappa' && (
                <div className="info-box">
                  <p>
                    The partners for{' '}
                    <strong className={`${character.element}`}>
                      {character.name}
                    </strong>{' '}
                    aren't available yet, because there's no data for Pure
                    Fiction including the character yet.
                  </p>
                </div>
              )} */}
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Common partners
                (AS)
              </div>
              <p>
                This section lists the characters that were most commonly paired
                with{' '}
                <strong className={`${character.element}`}>
                  {character.name}
                </strong>{' '}
                in the current phase of Apocalyptic Shadow.
              </p>
              {ApoDuos.filter(
                (char) => char.char.toLowerCase() === character.slug
              ).map((duo, idx) => {
                return (
                  <div key={idx} className="moc-duo on-moc ">
                    <div className={`single-char`}>
                      <Card className="avatar-card">
                        <HSRCharacter
                          slug={duo.char_1.toLowerCase()}
                          mode="icon"
                          enablePopover
                        />
                      </Card>
                      <div className="column percentage">
                        <p>{duo.app_rate_1}</p>
                      </div>
                    </div>
                    {duo.char_2 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_2.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_2}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_3 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_3.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_3}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_4 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_4.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_4}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_5 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_5.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_5}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_6 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_6.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_6}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_7 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_7.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_7}</p>
                        </div>
                      </div>
                    )}
                    {duo.char_8 != '-' && (
                      <div className={`single-char`}>
                        <Card className="avatar-card">
                          <HSRCharacter
                            slug={duo.char_8.toLowerCase()}
                            mode="icon"
                            enablePopover
                          />
                        </Card>
                        <div className="column percentage">
                          <p>{duo.app_rate_8}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* {character.slug === 'lingsha' && (
                <div className="info-box">
                  <p>
                    The partners for{' '}
                    <strong className={`${character.element}`}>
                      {character.name}
                    </strong>{' '}
                    aren't available yet, because there's no data for
                    Apocalyptic Shadow including the character yet.
                  </p>
                </div>
              )} */}
              {mocPfData
                .filter((char) => char.char.toLowerCase() === character.slug)
                .map((mocCharacter, index) => {
                  return (
                    <div key={index} className="moc-data">
                      <div className={`content-header ${character.element}`}>
                        <FontAwesomeIcon icon={faSquare} width="18" /> Eidolons
                      </div>
                      <p>
                        Eidolons data comes from players we have scanned who
                        used{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        in the current MoC cycle{' '}
                        <strong>({mocCharacter.sample_moc})</strong>, in the
                        current PF cycle{' '}
                        <strong>({mocCharacter.sample_pf})</strong> or in the
                        current AS cycle{' '}
                        <strong>({mocCharacter.sample_as})</strong> and had the
                        character placed in their Profile. Use the switcher
                        below to show{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        Eidolon performance in the endgame modes (Default MoC) -
                        the percentage doesn't change as it takes into account
                        all modes already.
                      </p>
                      <div className="mode-switcher">
                        <div className="text">Change mode:</div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'MOC' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('MOC')}
                        >
                          <p>MoC</p>
                        </div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'PF' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('PF')}
                        >
                          <p>PF</p>
                        </div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'AS' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('AS')}
                        >
                          <p>AS</p>
                        </div>
                      </div>
                      {character.eidolon.map((eidolon, index) => {
                        return (
                          <div
                            className="detailed-cones moc extra eidolon extended"
                            key={index}
                          >
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_0}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_0_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_0_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_0_as} score</>
                                  )}
                                </span>
                              </div>
                              <div className="empty">
                                <p>Eidolon 0</p>
                              </div>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_1}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_1_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_1_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_1_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 1</strong> -{' '}
                                    {eidolon.upgrade1Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade1Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_2}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_2_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_2_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_2_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 2</strong> -{' '}
                                    {eidolon.upgrade2Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade2Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_3}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_3_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_3_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_3_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 3</strong> -{' '}
                                    {eidolon.upgrade3Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade3Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_4}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_4_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_4_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_4_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 4</strong> -{' '}
                                    {eidolon.upgrade4Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade4Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_5}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_5_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_5_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_5_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 5</strong> -{' '}
                                    {eidolon.upgrade5Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade5Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                            <div className={`single-cone ${character.element}`}>
                              <div className="percentage">
                                <p>{mocCharacter.app_6}%</p>
                                <span>
                                  {currentMode === 'MOC' && (
                                    <>{mocCharacter.round_6_moc} cyc.</>
                                  )}
                                  {currentMode === 'PF' && (
                                    <>{mocCharacter.round_6_pf} score</>
                                  )}
                                  {currentMode === 'AS' && (
                                    <>{mocCharacter.round_6_as} score</>
                                  )}
                                </span>
                              </div>
                              <Accordion>
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>
                                    <strong>Eidolon 6</strong> -{' '}
                                    {eidolon.upgrade6Name}
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <div
                                      className={`skill-with-coloring eidolon ${character.element}`}
                                    >
                                      {renderRichText(
                                        eidolon.upgrade6Desc,
                                        options
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          </div>
                        );
                      })}
                      <div className={`content-header ${character.element}`}>
                        <FontAwesomeIcon icon={faSquare} width="18" /> Light
                        Cones
                      </div>
                      <p>
                        Light Cones are ranked with usage rate among the players
                        we have scanned who used{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        in the current MoC cycle{' '}
                        <strong>({mocCharacter.sample_moc})</strong>, in the
                        current PF cycle{' '}
                        <strong>({mocCharacter.sample_pf})</strong> or in the
                        current AS cycle{' '}
                        <strong>({mocCharacter.sample_as})</strong> and had the
                        character placed in their Profile. Use the switcher
                        below to show{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        Light Cones performance in the endgame modes (Default
                        MoC) - the percentage doesn't change as it takes into
                        account all modes already.
                      </p>
                      <div className="mode-switcher">
                        <div className="text">Change mode:</div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'MOC' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('MOC')}
                        >
                          <p>MoC</p>
                        </div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'PF' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('PF')}
                        >
                          <p>PF</p>
                        </div>
                        <div
                          className={`single-mode ${character.element} ${
                            currentMode === 'AS' ? 'active' : ''
                          }`}
                          onClick={() => setCurrentMode('AS')}
                        >
                          <p>AS</p>
                        </div>
                      </div>
                      {mocCharacter.weapons_1_app != 0 ? (
                        <>
                          <div className="detailed-cones moc extra extended">
                            {mocCharacter.weapons_1_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_1_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_1_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_1_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_1_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_1}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_2_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_2_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_2_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_2_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_2_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_2}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_3_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_3_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_3_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_3_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_3_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_3}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_4_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_4_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_4_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_4_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_4_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_4}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_5_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_5_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_5_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_5_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_5_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_5}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_6_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_6_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_6_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_6_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_6_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_6}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_7_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_7_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_7_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_7_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_7_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_7}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_8_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_8_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_8_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_8_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_8_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_8}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_9_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_9_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_9_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_9_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_9_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_9}
                                  mode="profile"
                                />
                              </div>
                            )}
                            {mocCharacter.weapons_10_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.weapons_10_app}%</p>
                                  <span>
                                    {currentMode === 'MOC' && (
                                      <>
                                        {mocCharacter.weapons_10_round_moc} cyc.
                                      </>
                                    )}
                                    {currentMode === 'PF' && (
                                      <>
                                        {mocCharacter.weapons_10_round_pf} score
                                      </>
                                    )}
                                    {currentMode === 'AS' && (
                                      <>
                                        {mocCharacter.weapons_10_round_as} score
                                      </>
                                    )}
                                  </span>
                                </div>
                                <HSRLightCone
                                  name={mocCharacter.weapons_10}
                                  mode="profile"
                                />
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="info-box">
                          <p>
                            <strong className={`${character.element}`}>
                              {character.name}
                            </strong>{' '}
                            <strong>
                              Light Cone information aren't available because
                              there's not enough data for them.
                            </strong>
                          </p>
                        </div>
                      )}
                      <div className={`content-header ${character.element}`}>
                        <FontAwesomeIcon icon={faSquare} width="18" /> Relic
                        Sets
                      </div>
                      <p>
                        Relic Sets are ranked with usage rate among the players
                        we have scanned who used{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        in the current MoC cycle{' '}
                        <strong>({mocCharacter.sample_moc})</strong>, in the
                        current PF cycle{' '}
                        <strong>({mocCharacter.sample_pf})</strong> or in the
                        current AS cycle{' '}
                        <strong>({mocCharacter.sample_as})</strong> and had the
                        character placed in their Profile. The results are
                        limited to <strong>top 5</strong> combinations.{' '}
                        <strong>Flex</strong> means 2 random Relic pieces that
                        don't combine into a Set.
                      </p>
                      {mocCharacter.artifacts_1_app != 0 ? (
                        <>
                          <h6>Relic Sets</h6>
                          <div className="detailed-cones moc extra planar">
                            {mocCharacter.artifacts_1_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.artifacts_1_app}%</p>
                                </div>
                                {mocCharacter.artifacts_1.includes(',') &&
                                mocCharacter.artifacts_1 !=
                                  'Watchmaker, Master of Dream Machinations' ? (
                                  <div className="double-set">
                                    {mocCharacter.artifacts_1_1 ===
                                      'ATK +12%' ||
                                    mocCharacter.artifacts_1_1 === 'BE +16%' ||
                                    mocCharacter.artifacts_1_1 === 'SPD +6%' ? (
                                      <>
                                        {mocCharacter.artifacts_1_1 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Musketeer of Wild Wheat"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_1_1 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Thief of Shooting Meteor"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_1_1 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Messenger Traversing Hackerspace"
                                            compactMode
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <HSRRelicSet
                                          name={mocCharacter.artifacts_1_1}
                                          compactMode
                                        />
                                      </>
                                    )}
                                    {mocCharacter.artifacts_1_2 === 'Flex' ? (
                                      <>
                                        <div className="flex-placeholder">
                                          Flex
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        {mocCharacter.artifacts_1_2 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Prisoner in Deep Confinement"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_1_2 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Watchmaker, Master of Dream Machinations"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_1_2 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Sacerdos' Relived Ordeal"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_1_2 !=
                                          'ATK +12%' &&
                                          mocCharacter.artifacts_1_2 !=
                                            'SPD +6%' &&
                                          mocCharacter.artifacts_1_2 !=
                                            'BE +16%' && (
                                            <HSRRelicSet
                                              name={mocCharacter.artifacts_1_2}
                                              compactMode
                                            />
                                          )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <HSRRelicSet
                                      name={mocCharacter.artifacts_1}
                                      compactMode
                                    />
                                  </>
                                )}
                              </div>
                            )}
                            {mocCharacter.artifacts_2_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.artifacts_2_app}%</p>
                                </div>
                                {mocCharacter.artifacts_2.includes(',') &&
                                mocCharacter.artifacts_2_1 !=
                                  'Watchmaker, Master of Dream Machinations' ? (
                                  <div className="double-set">
                                    {mocCharacter.artifacts_2_1 ===
                                      'ATK +12%' ||
                                    mocCharacter.artifacts_2_1 === 'BE +16%' ||
                                    mocCharacter.artifacts_2_1 === 'SPD +6%' ? (
                                      <>
                                        {mocCharacter.artifacts_2_1 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Musketeer of Wild Wheat"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_2_1 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Thief of Shooting Meteor"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_2_1 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Messenger Traversing Hackerspace"
                                            compactMode
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <HSRRelicSet
                                          name={mocCharacter.artifacts_2_1}
                                          compactMode
                                        />
                                      </>
                                    )}
                                    {mocCharacter.artifacts_2_2 === 'Flex' ? (
                                      <>
                                        <div className="flex-placeholder">
                                          Flex
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        {mocCharacter.artifacts_2_2 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Prisoner in Deep Confinement"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_2_2 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Watchmaker, Master of Dream Machinations"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_2_2 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Sacerdos' Relived Ordeal"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_2_2 !=
                                          'ATK +12%' &&
                                          mocCharacter.artifacts_2_2 !=
                                            'SPD +6%' &&
                                          mocCharacter.artifacts_2_2 !=
                                            'BE +16%' && (
                                            <HSRRelicSet
                                              name={mocCharacter.artifacts_2_2}
                                              compactMode
                                            />
                                          )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <HSRRelicSet
                                      name={mocCharacter.artifacts_2}
                                      compactMode
                                    />
                                  </>
                                )}
                              </div>
                            )}
                            {mocCharacter.artifacts_3_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.artifacts_3_app}%</p>
                                </div>
                                {mocCharacter.artifacts_3.includes(',') &&
                                mocCharacter.artifacts_3 !=
                                  'Watchmaker, Master of Dream Machinations' ? (
                                  <div className="double-set">
                                    {mocCharacter.artifacts_3_1 ===
                                      'ATK +12%' ||
                                    mocCharacter.artifacts_3_1 === 'BE +16%' ||
                                    mocCharacter.artifacts_3_1 === 'SPD +6%' ? (
                                      <>
                                        {mocCharacter.artifacts_3_1 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Musketeer of Wild Wheat"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_3_1 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Thief of Shooting Meteor"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_3_1 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Messenger Traversing Hackerspace"
                                            compactMode
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <HSRRelicSet
                                          name={mocCharacter.artifacts_3_1}
                                          compactMode
                                        />
                                      </>
                                    )}
                                    {mocCharacter.artifacts_3_2 === 'Flex' ? (
                                      <>
                                        <div className="flex-placeholder">
                                          Flex
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        {mocCharacter.artifacts_3_2 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Prisoner in Deep Confinement"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_3_2 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Watchmaker, Master of Dream Machinations"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_3_2 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Sacerdos' Relived Ordeal"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_3_2 !=
                                          'ATK +12%' &&
                                          mocCharacter.artifacts_3_2 !=
                                            'SPD +6%' &&
                                          mocCharacter.artifacts_3_2 !=
                                            'BE +16%' && (
                                            <HSRRelicSet
                                              name={mocCharacter.artifacts_3_2}
                                              compactMode
                                            />
                                          )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <HSRRelicSet
                                      name={mocCharacter.artifacts_3}
                                      compactMode
                                    />
                                  </>
                                )}
                              </div>
                            )}
                            {mocCharacter.artifacts_4_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.artifacts_4_app}%</p>
                                </div>
                                {mocCharacter.artifacts_4.includes(',') &&
                                mocCharacter.artifacts_4 !=
                                  'Watchmaker, Master of Dream Machinations' ? (
                                  <div className="double-set">
                                    {mocCharacter.artifacts_4_1 ===
                                      'ATK +12%' ||
                                    mocCharacter.artifacts_4_1 === 'BE +16%' ||
                                    mocCharacter.artifacts_4_1 === 'SPD +6%' ? (
                                      <>
                                        {mocCharacter.artifacts_4_1 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Musketeer of Wild Wheat"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_4_1 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Thief of Shooting Meteor"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_4_1 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Messenger Traversing Hackerspace"
                                            compactMode
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <HSRRelicSet
                                          name={mocCharacter.artifacts_4_1}
                                          compactMode
                                        />
                                      </>
                                    )}
                                    {mocCharacter.artifacts_4_2 === 'Flex' ? (
                                      <>
                                        <div className="flex-placeholder">
                                          Flex
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        {mocCharacter.artifacts_4_2 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Prisoner in Deep Confinement"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_4_2 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Watchmaker, Master of Dream Machinations"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_4_2 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Sacerdos' Relived Ordeal"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_4_2 !=
                                          'ATK +12%' &&
                                          mocCharacter.artifacts_4_2 !=
                                            'SPD +6%' &&
                                          mocCharacter.artifacts_4_2 !=
                                            'BE +16%' && (
                                            <HSRRelicSet
                                              name={mocCharacter.artifacts_4_2}
                                              compactMode
                                            />
                                          )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <HSRRelicSet
                                      name={mocCharacter.artifacts_4}
                                      compactMode
                                    />
                                  </>
                                )}
                              </div>
                            )}
                            {mocCharacter.artifacts_5_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.artifacts_5_app}%</p>
                                </div>
                                {mocCharacter.artifacts_5.includes(',') &&
                                mocCharacter.artifacts_5 !=
                                  'Watchmaker, Master of Dream Machinations' ? (
                                  <div className="double-set">
                                    {mocCharacter.artifacts_5_1 ===
                                      'ATK +12%' ||
                                    mocCharacter.artifacts_5_1 === 'BE +16%' ||
                                    mocCharacter.artifacts_5_1 === 'SPD +6%' ? (
                                      <>
                                        {mocCharacter.artifacts_5_1 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Musketeer of Wild Wheat"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_5_1 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Thief of Shooting Meteor"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_5_1 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Messenger Traversing Hackerspace"
                                            compactMode
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <HSRRelicSet
                                          name={mocCharacter.artifacts_5_1}
                                          compactMode
                                        />
                                      </>
                                    )}
                                    {mocCharacter.artifacts_5_2 === 'Flex' ? (
                                      <>
                                        <div className="flex-placeholder">
                                          Flex
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        {mocCharacter.artifacts_5_2 ===
                                          'ATK +12%' && (
                                          <HSRRelicSet
                                            name="Prisoner in Deep Confinement"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_5_2 ===
                                          'BE +16%' && (
                                          <HSRRelicSet
                                            name="Watchmaker, Master of Dream Machinations"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_5_2 ===
                                          'SPD +6%' && (
                                          <HSRRelicSet
                                            name="Sacerdos' Relived Ordeal"
                                            compactMode
                                          />
                                        )}
                                        {mocCharacter.artifacts_5_2 !=
                                          'ATK +12%' &&
                                          mocCharacter.artifacts_5_2 !=
                                            'SPD +6%' &&
                                          mocCharacter.artifacts_5_2 !=
                                            'BE +16%' && (
                                            <HSRRelicSet
                                              name={mocCharacter.artifacts_5_2}
                                              compactMode
                                            />
                                          )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <HSRRelicSet
                                      name={mocCharacter.artifacts_5}
                                      compactMode
                                    />
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <h6 className="with-margin">Planetary Sets</h6>
                          <div className="detailed-cones moc extra planar">
                            {mocCharacter.planars_1_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.planars_1_app}%</p>
                                </div>
                                <HSRRelicSet
                                  name={mocCharacter.planars_1}
                                  compactMode
                                />
                              </div>
                            )}
                            {mocCharacter.planars_2_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.planars_2_app}%</p>
                                </div>
                                <HSRRelicSet
                                  name={mocCharacter.planars_2}
                                  compactMode
                                />
                              </div>
                            )}
                            {mocCharacter.planars_3_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.planars_3_app}%</p>
                                </div>
                                <HSRRelicSet
                                  name={mocCharacter.planars_3}
                                  compactMode
                                />
                              </div>
                            )}
                            {mocCharacter.planars_4_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.planars_4_app}%</p>
                                </div>
                                <HSRRelicSet
                                  name={mocCharacter.planars_4}
                                  compactMode
                                />
                              </div>
                            )}
                            {mocCharacter.planars_5_app != 0 && (
                              <div
                                className={`single-cone ${character.element}`}
                              >
                                <div className="percentage">
                                  <p>{mocCharacter.planars_5_app}%</p>
                                </div>
                                <HSRRelicSet
                                  name={mocCharacter.planars_5}
                                  compactMode
                                />
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="info-box">
                          <p>
                            <strong className={`${character.element}`}>
                              {character.name}
                            </strong>{' '}
                            <strong>
                              Relic Sets information aren't available because
                              there's not enough data for them.
                            </strong>
                          </p>
                        </div>
                      )}
                      <div className={`content-header ${character.element}`}>
                        <FontAwesomeIcon icon={faSquare} width="18" /> Relic
                        Stats
                      </div>
                      <p>
                        Relic Stats are shown based on the Relics used by the
                        players we have scanned who used{' '}
                        <strong className={`${character.element}`}>
                          {character.name}
                        </strong>{' '}
                        in the current MoC cycle{' '}
                        <strong>({mocCharacter.sample_moc})</strong>, in the
                        current PF cycle{' '}
                        <strong>({mocCharacter.sample_pf})</strong> or in the
                        current AS cycle{' '}
                        <strong>({mocCharacter.sample_as})</strong> and had the
                        character placed in their Profile.
                      </p>
                      <h6>Main stats</h6>
                      <Row xs={1} xl={2} xxl={4} className="moc-stats">
                        <Col>
                          <div className="box">
                            <div className="stats-header">
                              <span>Body</span>
                            </div>
                            <div className="moc-stats-list">
                              {mocCharacter.body_stats_1 != '' && (
                                <div className="single-stat">
                                  <HSRStat stat={mocCharacter.body_stats_1} /> -{' '}
                                  <strong>
                                    {mocCharacter.body_stats_1_app}%
                                  </strong>
                                </div>
                              )}
                              {mocCharacter.body_stats_2 != '' &&
                                mocCharacter.body_stats_2 != '-' && (
                                  <div className="single-stat with-bg">
                                    <HSRStat stat={mocCharacter.body_stats_2} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.body_stats_2_app}%
                                    </strong>
                                  </div>
                                )}
                              {mocCharacter.body_stats_3 != '' &&
                                mocCharacter.body_stats_3 != '-' && (
                                  <div className="single-stat">
                                    <HSRStat stat={mocCharacter.body_stats_3} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.body_stats_3_app}%
                                    </strong>
                                  </div>
                                )}
                            </div>
                          </div>
                        </Col>
                        <Col>
                          <div className="box">
                            <div className="stats-header">
                              <span>Feet</span>
                            </div>
                            <div className="moc-stats-list">
                              {mocCharacter.feet_stats_1 != '' &&
                                mocCharacter.feet_stats_1 != '-' && (
                                  <div className="single-stat">
                                    <HSRStat stat={mocCharacter.feet_stats_1} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.feet_stats_1_app}%
                                    </strong>
                                  </div>
                                )}
                              {mocCharacter.feet_stats_2 != '' &&
                                mocCharacter.feet_stats_2 != '-' && (
                                  <div className="single-stat with-bg">
                                    <HSRStat stat={mocCharacter.feet_stats_2} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.feet_stats_2_app}%
                                    </strong>
                                  </div>
                                )}
                              {mocCharacter.feet_stats_3 != '' &&
                                mocCharacter.feet_stats_3 != '-' && (
                                  <div className="single-stat">
                                    <HSRStat stat={mocCharacter.feet_stats_3} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.feet_stats_3_app}%
                                    </strong>
                                  </div>
                                )}
                            </div>
                          </div>
                        </Col>
                        <Col>
                          <div className="box">
                            <div className="stats-header">
                              <span>Planar Sphere</span>
                            </div>
                            <div className="moc-stats-list">
                              {mocCharacter.sphere_stats_1 != '' && (
                                <div className="single-stat">
                                  <HSRStat stat={mocCharacter.sphere_stats_1} />{' '}
                                  -{' '}
                                  <strong>
                                    {mocCharacter.sphere_stats_1_app}%
                                  </strong>
                                </div>
                              )}
                              {mocCharacter.sphere_stats_2 != '' &&
                                mocCharacter.sphere_stats_2 != '-' && (
                                  <div className="single-stat with-bg">
                                    <HSRStat
                                      stat={mocCharacter.sphere_stats_2}
                                    />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.sphere_stats_2_app}%
                                    </strong>
                                  </div>
                                )}
                              {mocCharacter.sphere_stats_3 != '' &&
                                mocCharacter.sphere_stats_3 != '-' && (
                                  <div className="single-stat">
                                    <HSRStat
                                      stat={mocCharacter.sphere_stats_3}
                                    />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.sphere_stats_3_app}%
                                    </strong>
                                  </div>
                                )}
                            </div>
                          </div>
                        </Col>
                        <Col>
                          <div className="box">
                            <div className="stats-header">
                              <span>Link Rope</span>
                            </div>
                            <div className="moc-stats-list">
                              {mocCharacter.rope_stats_1 != '' && (
                                <div className="single-stat">
                                  <HSRStat stat={mocCharacter.rope_stats_1} /> -{' '}
                                  <strong>
                                    {mocCharacter.rope_stats_1_app}%
                                  </strong>
                                </div>
                              )}
                              {mocCharacter.rope_stats_2 != '' &&
                                mocCharacter.rope_stats_2 != '-' && (
                                  <div className="single-stat with-bg">
                                    <HSRStat stat={mocCharacter.rope_stats_2} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.rope_stats_2_app}%
                                    </strong>
                                  </div>
                                )}
                              {mocCharacter.rope_stats_3 != '' &&
                                mocCharacter.rope_stats_3 != '-' && (
                                  <div className="single-stat">
                                    <HSRStat stat={mocCharacter.rope_stats_3} />{' '}
                                    -{' '}
                                    <strong>
                                      {mocCharacter.rope_stats_3_app}%
                                    </strong>
                                  </div>
                                )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row xs={1} xl={2} xxl={2} className="moc-stats">
                        <Col>
                          <h6 className="with-margin">Average stats</h6>
                          <div className="box">
                            <div className="stats-header">
                              <span>Average stats</span>
                            </div>
                            <div className="moc-stats-list">
                              <div className="single-stat">
                                <HSRStat stat="HP" /> -{' '}
                                <strong>{mocCharacter.max_hp}</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="ATK" /> -{' '}
                                <strong>{mocCharacter.atk}</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="DEF" /> -{' '}
                                <strong>{mocCharacter.dfns}</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="SPD" /> -{' '}
                                <strong>{mocCharacter.speed}</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="CRIT Rate" /> -{' '}
                                <strong>{mocCharacter.crate}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="CRIT DMG" /> -{' '}
                                <strong>{mocCharacter.cdmg}%</strong>
                              </div>
                              <div className="single-stat">
                                {character.element === 'Fire' && (
                                  <HSRStat stat="Fire DMG" />
                                )}
                                {character.element === 'Wind' && (
                                  <HSRStat stat="Wind DMG" />
                                )}
                                {character.element === 'Ice' && (
                                  <HSRStat stat="Ice DMG" />
                                )}
                                {character.element === 'Quantum' && (
                                  <HSRStat stat="Quantum DMG" />
                                )}
                                {character.element === 'Imaginary' && (
                                  <HSRStat stat="Imaginary DMG" />
                                )}
                                {character.element === 'Lightning' && (
                                  <HSRStat stat="Lightning DMG" />
                                )}
                                {character.element === 'Physical' && (
                                  <HSRStat stat="Physical DMG" />
                                )}
                                - <strong>{mocCharacter.dmg_boost}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="Energy Regen Rate" /> -{' '}
                                <strong>{mocCharacter.energy_regen}%</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="effectRes" /> -{' '}
                                <strong>{mocCharacter.effect_res}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="Effect Hit Rate" /> -{' '}
                                <strong>{mocCharacter.effect_rate}%</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="Break Effect" /> -{' '}
                                <strong>{mocCharacter.break_effect}%</strong>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col>
                          <h6 className="with-margin">Average sub stats</h6>
                          <div className="box">
                            <div className="stats-header">
                              <span>Average sub stats</span>
                            </div>
                            <div className="moc-stats-list">
                              <div className="single-stat">
                                <HSRStat stat="HP" /> -{' '}
                                <strong>{mocCharacter.hp_sub}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="ATK" /> -{' '}
                                <strong>{mocCharacter.atk_sub}%</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="DEF" /> -{' '}
                                <strong>{mocCharacter.def_sub}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="SPD" /> -{' '}
                                <strong>{mocCharacter.spd_sub}</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="CRIT Rate" /> -{' '}
                                <strong>{mocCharacter.crate_sub}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="CRIT DMG" /> -{' '}
                                <strong>{mocCharacter.cdmg_sub}%</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="effectRes" /> -{' '}
                                <strong>{mocCharacter.res_sub}%</strong>
                              </div>
                              <div className="single-stat with-bg">
                                <HSRStat stat="Effect Hit Rate" /> -{' '}
                                <strong>{mocCharacter.ehr_sub}%</strong>
                              </div>
                              <div className="single-stat">
                                <HSRStat stat="Break Effect" /> -{' '}
                                <strong>{mocCharacter.break_sub}%</strong>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <div className={`content-header ${character.element}`}>
                        <FontAwesomeIcon icon={faSquare} width="18" /> Other
                        stats
                      </div>
                      <Row xs={1} xl={2} xxl={2} className="moc-stats">
                        <Col>
                          <div className="box">
                            <div className="stats-header">
                              <span>Average stats</span>
                            </div>
                            <div className="moc-stats-list">
                              <div className="single-stat">
                                Character level -{' '}
                                <strong>{mocCharacter.char_lvl}</strong>
                              </div>
                              <div className="single-stat with-bg">
                                Light Cone level -{' '}
                                <strong>{mocCharacter.light_cone_lvl}</strong>
                              </div>
                              <div className="single-stat">
                                Basic level -{' '}
                                <strong>{mocCharacter.attack_lvl}</strong>
                              </div>
                              <div className="single-stat with-bg">
                                Skill level -{' '}
                                <strong>{mocCharacter.skill_lvl}</strong>
                              </div>
                              <div className="single-stat">
                                Ultimate level -{' '}
                                <strong>{mocCharacter.ultimate_lvl}</strong>
                              </div>
                              <div className="single-stat with-bg">
                                Talent level -{' '}
                                <strong>{mocCharacter.talent_lvl}</strong>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
            </>
          ) : (
            <>
              <div className="info-box">
                <p>
                  <strong className={`${character.element}`}>
                    {character.name}
                  </strong>{' '}
                  <strong>Memory of Chaos stats aren't available yet.</strong>
                </p>
              </div>
            </>
          )}
        </div>
        <div
          className={`tab-inside ${
            currentTab === 'Performance' ? 'active' : ''
          }`}
        >
          <div className={`mobile-header ${character.element}`}>
            <p>
              <FontAwesomeIcon icon={faCircleInfo} width="18" />
              Calculations
            </p>
          </div>
          {character.slug === 'fu-xuan' && <ProfileFuEHP />}
          {(character.slug === 'sparkle' ||
            character.slug === 'bronya' ||
            character.slug === 'sunday') && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Sparkle vs Bronya
                vs Sunday - who is better?
              </div>
              <ProfileSparkleBronya />
            </>
          )}
          {(character.slug === 'trailblazer-harmony' ||
            character.slug === 'tingyun-fugue') && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Harmony MC vs
                Fugue
              </div>
              <ProfileMCvsFugue />
            </>
          )}
          {character.slug === 'aventurine' && <AveProfileCalcs />}
          {character.slug === 'trailblazer-harmony' && (
            <>
              <ProfileHarmonyEnergy />
              <ProfileHarmonyDPS />
            </>
          )}
          {character.slug === 'robin' && (
            <>
              <div className={`content-header ${character.element}`}>
                <FontAwesomeIcon icon={faSquare} width="18" /> Calculations
              </div>
              <RobinCalcs />
            </>
          )}
          {character.slug != 'huohuo' &&
            character.slug != 'aventurine' &&
            character.slug != 'fu-xuan' &&
            character.slug != 'trailblazer-harmony' &&
            character.slug != 'tingyun-fugue' &&
            character.slug != 'robin' &&
            character.slug != 'sparkle' &&
            character.slug != 'sunday' &&
            character.slug != 'bronya' && (
              <>
                {dpsRanking.length === 0 ? (
                  <>
                    <div className={`content-header ${character.element}`}>
                      <FontAwesomeIcon icon={faSquare} width="18" />{' '}
                      Calculations
                    </div>
                    {character.slug === 'jiaoqiu' ||
                    character.slug === 'the-herta' ||
                    character.slug === 'mydei' ||
                    character.slug === 'castorice' ||
                    character.slug === 'tribbie' ? (
                      <>
                        {character.slug === 'jiaoqiu' && <ProfileJiaoqiuDPS />}
                        {character.slug === 'the-herta' && <ProfileHertaDPS />}
                        {character.slug === 'castorice' && (
                          <ProfileCastoriceDPS />
                        )}
                        {character.slug === 'tribbie' && <ProfileTribbieDPS />}
                        {character.slug === 'mydei' && <ProfileMydeiDPS />}
                      </>
                    ) : (
                      <>
                        <div className="info-box">
                          <p>
                            <strong className={`${character.element}`}>
                              {character.name}
                            </strong>{' '}
                            <strong>calculations aren't available yet.</strong>
                          </p>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`content-header ${character.element}`}>
                      <FontAwesomeIcon icon={faSquare} width="18" /> Damage
                      Output
                    </div>
                    <Alert variant="primary alert-red ">
                      <p>
                        <strong>
                          Don't use the damage output calculations as an
                          indication on who is worth pulling or investing as
                          those are just simulations and in day-to-day gameplay
                          there's a lot variables
                        </strong>
                        . Use the numbers only to supplement your knowledge and
                        treat them as the 'floor' of a character's damage output
                        which you can further scale with proper team and
                        investment.
                      </p>
                    </Alert>
                    <p>
                      The simulation showcases characters{' '}
                      <strong>
                        damage potential in the 5 cycle turn limit of Memory of
                        Chaos against 1 target and 3 targets
                      </strong>
                      . The section below has all the rules listed.
                    </p>
                    <Accordion className="sim-rules">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Simulation rules</Accordion.Header>
                        <Accordion.Body>
                          <p>
                            <strong>General:</strong>
                          </p>
                          <ul>
                            <li>
                              Combat is simulated at 5 cycles of in-game time or
                              550 Action Points,
                            </li>
                            <li>
                              Units are considered to gain 5 additional energy
                              per rotation through combat (taking a hit, killing
                              an enemy or through party members and their Light
                              Cone),
                            </li>
                            <li>
                              Characters and Light Cones are calculated at
                              maximum output starting battle with ramp time
                              removed from stacking buffs (intended to give a
                              constant evaluation of power not dependant on
                              length of fight that can be used to assess maximum
                              power). Additional information about loss of
                              damage due to ramp time can be round attached to
                              related Light Cones, relics and characters,
                            </li>
                            <li>
                              Character rotations are smoothed removing
                              breakpoints relating to Speed and Energy
                              (finishing a fight with 90% energy full or 2 AV
                              off next ability leading to skewed results).
                            </li>
                          </ul>
                          <p>
                            <strong>Enemies:</strong>
                          </p>
                          <ul>
                            <li>
                              Enemies are considered to always have a Break Bar
                              resulting in units all doing 90% of real damage
                              (this doesn't apply to Break characters - they
                              simulate the Break bar correctly),
                            </li>
                            <li>Enemies Speed is set to 140,</li>
                            <li>
                              Enemies are assumed to be at 100% of HP at start
                              of battle and to die at exactly at the end of
                              combat. Which means halfway through the fight they
                              are at 50% HP,
                            </li>
                            <li>Enemies are assumed to have 0 Resistance,</li>
                            <li>
                              Enemies are always considered vulnerable to the
                              attackers element,
                            </li>
                            <li>
                              Enemies are considered level 95 and with DEF
                              included.
                            </li>
                          </ul>
                          <p>
                            <strong>Units</strong>:
                          </p>
                          <ul>
                            <li>All units are level 80,</li>
                            <li>
                              Traces are all unlocked, skills at Level 10 and
                              Basic at Level 6,
                            </li>
                            <li>
                              All units have fully upgraded 5 star relics with
                              24 desired sub stats out of a possible 54,
                            </li>
                            <li>
                              All units use their Best In Slot 2 and 4 piece
                              sets,
                            </li>
                            <li>
                              All units use Light Cones that are realistically
                              obtainable by all player (excluding Limited LC).
                            </li>
                          </ul>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                    {character.slug === 'march-7th-swordmaster' && (
                      <>
                        <p>
                          <strong className="red">Important!</strong> We
                          calculated E3 as the base of March 7th - The Hunt
                          because we will receive enough Eidolons from her event
                          to raise her to E3 instantly. The calculations are
                          also for her Crit-based build, not the Break one.
                        </p>
                      </>
                    )}
                    {character.slug === 'aglaea' && (
                      <>
                        <p>
                          <strong className="red">Important!</strong> Aglaea's
                          total damage output benefits far more from her team
                          than other characters. As such comparing the raw
                          figures to other characters calculations is not an
                          accurate comparison (even more than usual, as we know
                          a lot of you still do it). We've included the
                          essentials required to ensure her Eidolons, relics and
                          Light Cones are represented accurately, but with full
                          team buffs and specific synergies that come with the
                          Remembrance path, she can match or exceed other top
                          tier damage dealers.
                        </p>
                        <p>
                          Also, how beneficial Aglaea's E1 ends up being is
                          highly reliant on her access to energy and her ability
                          to chain together Ultimates before and after it's
                          active. If you're able to occassionally or
                          consistantly chain together Ultimates at E0 you can
                          consider the benefit E1 provides to be significantly
                          lower. If you're unable to chain together any or a
                          minimal amount of Ultimates the represented number
                          reflects the value E1 will provide.
                        </p>
                      </>
                    )}
                    {(character.slug === 'gallagher' ||
                      character.slug === 'lingsha' ||
                      character.slug === 'firefly' ||
                      character.slug === 'rappa') && (
                      <>
                        <p>
                          <strong className="red">Important!</strong> The
                          calculation assume you're pairing{' '}
                          <strong>Trailblazer (Harmony)</strong> with{' '}
                          <strong className={`${character.element}`}>
                            {character.name}
                          </strong>{' '}
                          in the team to allow for <strong>Super Break</strong>{' '}
                          to occur.{' '}
                          {character.slug === 'rappa' && (
                            <>
                              Rappa’s E1 is displayed as if it would not grant
                              her a faster rotation via lower action count per
                              Ultimate. In the event it does the performance of
                              this Eidolon increases dramatically.
                            </>
                          )}
                        </p>
                      </>
                    )}
                    {character.slug === 'feixiao' && (
                      <>
                        <p>
                          <strong className="red">Important!</strong> The
                          calculation assume you're pairing{' '}
                          <strong>Feixiao</strong> with FUA charactrs to enable
                          her Ultimate to be charged in a timely manner. The
                          damage from the other characters isn't included in the
                          calculations.
                        </p>
                      </>
                    )}
                    {character.slug === 'acheron' && (
                      <>
                        <p>
                          <strong className="red">Important!</strong> The
                          calculation assume you're pairing{' '}
                          <strong>Acheron</strong> with 2x Nihility teammates
                          for the purpose of her A4 and providing debuffs for
                          her.
                        </p>
                      </>
                    )}
                    <Row xs={1} xl={2} className="dps-comparison">
                      <Col>
                        <div className="box">
                          <h5>{dpsRanking[0].name} in 1 Target scenario</h5>
                          <div
                            className={`simulation-container ${character.slug}`}
                          >
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E6)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-1`}
                                    style={{ width: '100%' }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps">
                                      {dpsRanking[0].single_dmg.damage_e6.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e6 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e6 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e6 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E5)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-2`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage_e5 /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps">
                                      {dpsRanking[0].single_dmg.damage_e5.toLocaleString()}{' '}
                                      dmg
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e5 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e5 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e5 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E4)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-3`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage_e4 /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps">
                                      {dpsRanking[0].single_dmg.damage_e4.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e4 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e4 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e4 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E3)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-4`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage_e3 /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps dark">
                                      {dpsRanking[0].single_dmg.damage_e3.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e3 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e3 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e3 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E2)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-5`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage_e2 /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps dark">
                                      {dpsRanking[0].single_dmg.damage_e2.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e2 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e2 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e2 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E1)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-6`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage_e1 /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps dark">
                                      {dpsRanking[0].single_dmg.damage_e1.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg
                                        .skillPoints_e1 != 0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints_e1 * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">
                                      {(
                                        (dpsRanking[0].single_dmg.damage_e1 /
                                          dpsRanking[0].single_dmg.damage) *
                                        100
                                      ).toFixed(2)}
                                      %
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="simulation-row">
                              <div className="character">
                                <HSRCharacter
                                  slug={dpsRanking[0].slug}
                                  mode="icon"
                                  enablePopover
                                />
                              </div>
                              <div className="chart">
                                <div className="info-2">
                                  <p>{dpsRanking[0].name} (E0)</p>
                                </div>
                                <div className="dps-bar-outside">
                                  <div
                                    className={`dps-bar top-7`}
                                    style={{
                                      width:
                                        (dpsRanking[0].single_dmg.damage /
                                          dpsRanking[0].single_dmg.damage_e6) *
                                          100 +
                                        '%'
                                    }}
                                  ></div>
                                  <div className="data">
                                    <span className="dps dark">
                                      {dpsRanking[0].single_dmg.damage.toLocaleString()}{' '}
                                      dmg{' '}
                                      {dpsRanking[0].single_dmg.skillPoints !=
                                        0 && (
                                        <>
                                          (
                                          {dpsRanking[0].single_dmg
                                            .skillPoints * -1}{' '}
                                          SP)
                                        </>
                                      )}
                                    </span>
                                    <span className="percent">100.00%</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                      {character.slug != 'luka' &&
                        character.slug != 'seele' &&
                        character.slug != 'yanqing' &&
                        character.slug != 'feixiao' &&
                        character.slug != 'sushang' &&
                        character.slug != 'xueyi' &&
                        character.slug != 'boothill' &&
                        character.slug != 'silver-wolf' &&
                        character.slug != 'march-7th-swordmaster' &&
                        character.slug != 'topaz' &&
                        character.slug != 'moze' &&
                        character.slug != 'dan-heng' &&
                        character.slug != 'dr-ratio' && (
                          <Col>
                            <div className="box">
                              <h5>
                                {dpsRanking[0].name} in 3 Targets scenario
                              </h5>
                              <div
                                className={`simulation-container ${character.slug}`}
                              >
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E6)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-1`}
                                        style={{ width: '100%' }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps">
                                          {dpsRanking[0].aoe_dmg.damage_e6.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e6 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e6 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E5)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-2`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage_e5 /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps">
                                          {dpsRanking[0].aoe_dmg.damage_e5.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e5 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e5 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E4)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-3`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage_e4 /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps">
                                          {dpsRanking[0].aoe_dmg.damage_e4.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e4 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e4 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E3)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-4`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage_e3 /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps dark">
                                          {dpsRanking[0].aoe_dmg.damage_e3.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e3 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e3 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E2)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-5`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage_e2 /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps dark">
                                          {dpsRanking[0].aoe_dmg.damage_e2.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e2 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e2 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E1)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-6`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage_e1 /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps dark">
                                          {dpsRanking[0].aoe_dmg.damage_e1.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg
                                            .skillPoints_e1 * -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">
                                          {(
                                            (dpsRanking[0].aoe_dmg.damage_e1 /
                                              dpsRanking[0].aoe_dmg.damage) *
                                            100
                                          ).toFixed(2)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="simulation-row">
                                  <div className="character">
                                    <HSRCharacter
                                      slug={dpsRanking[0].slug}
                                      mode="icon"
                                      enablePopover
                                    />
                                  </div>
                                  <div className="chart">
                                    <div className="info-2">
                                      <p>{dpsRanking[0].name} (E0)</p>
                                    </div>
                                    <div className="dps-bar-outside">
                                      <div
                                        className={`dps-bar top-7`}
                                        style={{
                                          width:
                                            (dpsRanking[0].aoe_dmg.damage /
                                              dpsRanking[0].aoe_dmg.damage_e6) *
                                              100 +
                                            '%'
                                        }}
                                      ></div>
                                      <div className="data">
                                        <span className="dps dark">
                                          {dpsRanking[0].aoe_dmg.damage.toLocaleString()}{' '}
                                          dmg (
                                          {dpsRanking[0].aoe_dmg.skillPoints *
                                            -1}{' '}
                                          SP)
                                        </span>
                                        <span className="percent">100.00%</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        )}
                    </Row>
                    {dpsRanking[0].disclaimer && (
                      <>
                        <p>
                          <strong>
                            Disclaimer for this particular calculations:
                          </strong>
                        </p>
                        <p>{dpsRanking[0].disclaimer}</p>
                      </>
                    )}
                    <p>
                      <strong>
                        And here are the Relics and the Light Cone used in the
                        calculations above:
                      </strong>
                    </p>
                    <h6 className="header">Light Cone</h6>
                    <div className="build-cones perf">
                      <Row xs={1} xl={1} xxl={1} className="simple-cones">
                        <Col>
                          <HSRLightCone
                            slug={dpsRanking[0].cone}
                            mode="profile"
                            superImp={dpsRanking[0].cone_s}
                          />
                        </Col>
                      </Row>
                    </div>
                    <div className="section-build perf">
                      <div className="build-relics perf">
                        <Row xs={1} xl={2} xxl={2} className="relics">
                          <Col>
                            <h6 className="header">Relic Set</h6>
                            <div className="relic-sets-rec">
                              {dpsRanking[0].relic_set_2 != '' ? (
                                <>
                                  <Row className="split-sets">
                                    <Col className="first" sm="12" md="6">
                                      <HSRRelicSet
                                        name={dpsRanking[0].relic_set_1}
                                        compactMode
                                      />
                                    </Col>
                                    <span className="show-on-mobile">+</span>
                                    <Col className="second" sm="12" md="6">
                                      <HSRRelicSet
                                        name={dpsRanking[0].relic_set_2}
                                        compactMode
                                      />
                                    </Col>
                                  </Row>
                                </>
                              ) : (
                                <HSRRelicSet
                                  name={dpsRanking[0].relic_set_1}
                                  compactMode
                                />
                              )}
                            </div>
                          </Col>
                          <Col>
                            <h6 className="header">Planetary Sets</h6>
                            <div className="relic-sets-rec">
                              <HSRRelicSet
                                name={dpsRanking[0].relic_set_3}
                                compactMode
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                      <h6 className="header">Relic Stats</h6>
                      <div className="build-stats">
                        <Row xs={1} xl={2} xxl={4} className="main-stats">
                          <Col>
                            <div className="box">
                              <div className="stats-header">
                                <span>Body</span>
                              </div>
                              <div className="list-stats">
                                <HSRStat stat={dpsRanking[0].body} />
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="box">
                              <div className="stats-header">
                                <span>Feet</span>
                              </div>
                              <div className="list-stats">
                                <HSRStat stat={dpsRanking[0].feet} />
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="box">
                              <div className="stats-header">
                                <span>Planar Sphere</span>
                              </div>
                              <div className="list-stats">
                                <HSRStat stat={dpsRanking[0].sphere} />
                              </div>
                            </div>
                          </Col>
                          <Col>
                            <div className="box">
                              <div className="stats-header">
                                <span>Link Rope</span>
                              </div>
                              <div className="list-stats">
                                <HSRStat stat={dpsRanking[0].rope} />
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="box sub-stats">
                              <span>Substats:</span>
                              <p>{dpsRanking[0].substats}</p>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
        </div>
        <div className="fuse-ad-placeholder" data-fuse="22844297232"></div>
      </>
    </DashboardLayout>
  );
};

export default HSRCharacterDetails;

export const Head: React.FC<IProps> = ({ data }) => {
  const character = data.currentUnit.nodes[0];

  return (
    <Seo
      title={character.name + ' Guide and Build | Honkai: Star Rail | Prydwen'}
      description={
        character.name +
        ' from Honkai: Star Rail. Best builds, Relics, Light Cones, guides and other information.'
      }
      image={character.smallImage}
    />
  );
};

export const pageQuery = graphql`
  query ($contentfulId: String) {
    currentUnit: allContentfulHsrCharacter(
      filter: { id: { eq: $contentfulId } }
    ) {
      nodes {
        ...HsrCharacterFieldsFragment
      }
    }
  }
`;
